/**
 * V2-506: Config management.
 *
 * - Loads `/v2/config/active` to populate the form.
 * - Every save POSTs a **full snapshot** (mirrors data-api's full-snapshot
 *   validator). The client-side gate enforces it BEFORE the request so
 *   the user sees missing-field errors inline.
 * - Saving creates a new config version only; activation remains deferred.
 */
import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  Alert, Badge, Button, Card, Checkbox, Code, Collapse, Divider, Grid, Group, NumberInput, Select,
  Stack, Switch, Text, TextInput, Textarea, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconClock, IconDeviceFloppy, IconUpload } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiClientError } from "../api/client";
import { ErrorState, LoadingState } from "../components/ui";
import { KRONOS_FIELD_DESCRIPTIONS, KRONOS_MODEL_OPTIONS, KRONOS_SIGNATURE_HINT, kronosSignatureFromParams } from "../lib/kronosFields";
import { COUNTERPART_FIELD_DESCRIPTIONS } from "../lib/counterpartFields";
import { FAVORITE_HOLD_FIELD_DESCRIPTIONS, FAVORITE_HOLD_FIELD_LABELS } from "../lib/favoriteHoldFields";
import { candleTimeframeOptionsFor, timeframeLabel } from "../lib/candleTimeframes";
import { FeatureMetaBadges } from "../lib/featureMeta";
import { KronosCachePicker } from "../components/KronosCachePicker";
import { useMarket } from "../hooks/useMarket";
import { monoStyle } from "../theme";

const REQUIRED_FIELDS = [
  "trade_size_usdc","min_odds","max_odds",
  "min_matching_candles","candles_to_check",
  "decision_time_seconds","monitor_after_decision","monitor_interval_seconds",
  "order_type","slippage_ticks",
  "gtc_auto_cancel_on_drift","gtc_recheck_seconds","gtc_cancel_unfilled_after_seconds",
  "fee_rate","fee_exponent",
  "allow_live_candle","live_candle_minute",
  // V3 multi-market candle controls. Stored as integers (seconds /
  // count) -- both ``0`` is the legacy "use market defaults" value.
  "candle_timeframe_seconds","skip_first_candles",
  // Open-price direction gate + candle override + cushion. See trading_bot.config.
  "open_price_gate_enabled","open_price_override_enabled","open_price_override_within_seconds",
  "open_price_cushion_enabled","open_price_cushion_min_bps",
  "prev_windows_check_enabled","prev_windows_count","prev_windows_min_matches",
  // Kronos forecast gate. See trading_bot.config for semantics.
  "kronos_check_enabled","kronos_require_direction","kronos_min_confidence",
  "kronos_model","kronos_lookback","kronos_candle_timeframe_seconds",
  "kronos_sample_count","kronos_temperature","kronos_top_p",
] as const;

/**
 * Resolve the effective candle width (seconds) used by the engine for a
 * given config + market. Mirrors ``_candle_agreement``'s fallback: 0
 * means "use the market's bucket_seconds".
 */
function effectiveTimeframeSeconds(configValue: number, marketBucketSeconds: number): number {
  return configValue && configValue > 0 ? configValue : Math.max(60, marketBucketSeconds);
}

interface FormState {
  config_name: string;
  trade_size_usdc: string;
  min_odds: string;
  max_odds: string;
  no_buy_window_seconds: number;
  no_sell_window_seconds: number;
  decision_time: string;
  candle_filter_enabled: boolean;
  min_matching_candles: number;
  candles_to_check: number;
  allow_live_candle: boolean;
  live_candle_minute: number;
  /**
   * V3 multi-market: candle width override (seconds). ``0`` keeps the
   * legacy behaviour (engine falls back to ``market.bucket_seconds``).
   */
  candle_timeframe_seconds: number;
  /** Drop the lowest-index N qualifying candles before evaluation. */
  skip_first_candles: number;

  order_type: string;
  slippage_ticks: number;
  gtc_auto_cancel_on_drift: boolean;
  gtc_recheck_seconds: number;
  gtc_cancel_unfilled_after_seconds: number;
  allow_multiple_orders_per_window: boolean;
  entry_confirm_seconds: number;
  sim_execution_delay_seconds: number;
  take_profit_order_type: string;
  exit_order_type: string;
  exit_slippage_ticks: number;
  exit_require_underlying_breach: boolean;
  exit_underlying_breach_bps: number;
  // Entry-quality gates (trading_bot/entry_quality.py).
  peak_drawdown_gate_enabled: boolean;
  max_drop_from_peak_odds: number;
  min_price_excursion_retention: number;
  vol_cushion_gate_enabled: boolean;
  vol_cushion_min_z: number;
  underlying_velocity_gate_enabled: boolean;
  underlying_velocity_lookback_seconds: number;
  underlying_velocity_min_bps: number;
  open_side_gate_enabled: boolean;
  open_side_streak_seconds: number;
  max_open_crosses: number;
  max_spread_gate_enabled: boolean;
  max_spread: number;
  counterpart_confirm_gate_enabled: boolean;
  counterpart_confirm_mode: string;
  counterpart_confirm_max_opposing_bps: number;
  kronos_min_edge_over_ask: number;
  fee_rate: string;
  fee_exponent: number;

  stop_loss_enabled: boolean;
  stop_loss_threshold: string;
  stop_loss_trigger_within_seconds: number;
  stop_loss_btc_delta_enabled: boolean;
  stop_loss_btc_delta_dollars: string;
  stop_loss_btc_poll_interval_seconds: number;
  stop_loss_btc_delta_mode: string;
  stop_loss_btc_confirm_seconds: number;
  stop_loss_last_candle_instant: boolean;
  stop_loss_last_candle_seconds: number;

  take_profit_enabled: boolean;
  take_profit_threshold: string;

  skip_after_wins_enabled: boolean;
  skip_after_wins_threshold: number;
  skip_after_wins_losses_to_resume: number;

  cascading_sizes_enabled: boolean;
  cascading_sizes: string;

  compounding_enabled: boolean;
  compounding_win_pct: number;
  compounding_loss_pct: number;
  compounding_max_size_usdc: number;
  compounding_stop_after_wins: number;
  compounding_stop_size_stay: boolean;
  compounding_resume_from_saved: boolean;

  late_buy_enabled: boolean;
  late_buy_within_seconds: number;
  late_buy_trade_size_usdc: string;

  force_buy_enabled: boolean;

  trade_time_ranges_enabled: boolean;
  trade_time_ranges: string;

  trade_days_enabled: boolean;
  trade_days_allowed: string;

  dry_run: boolean;
  log_level: string;
  log_file: string;
  state_file: string;
  display_timezone: string;
  price_feed: string;

  odds_momentum_enabled: boolean;
  odds_momentum_lookback: number;
  odds_momentum_required_increasing: number;
  odds_momentum_ignore_last_seconds: number;
  odds_momentum_use_unique_odds: boolean;
  odds_momentum_avg_lookback: number;
  odds_momentum_avg_max_diff: number;

  momentum_sell_enabled: boolean;
  momentum_sell_window_type: string;
  momentum_sell_window_value: number;
  momentum_sell_sl_enabled: boolean;
  momentum_sell_sl_threshold_type: string;
  momentum_sell_sl_threshold_value: number;
  momentum_sell_sl_require_seconds: number;
  momentum_sell_sl_start_at_seconds_left: number;
  momentum_sell_tp_enabled: boolean;
  momentum_sell_tp_threshold_type: string;
  momentum_sell_tp_threshold_value: number;
  momentum_sell_tp_min_seconds_below: number;
  momentum_sell_tp_start_at_seconds_left: number;
  momentum_sell_profit_is_win: boolean;
  momentum_sell_loss_breaks_streak: boolean;
  loss_streak_continue_threshold_usdc: number;

  pre_order_metrics_enabled: boolean;
  pre_order_up_avg_odds_filter_enabled: boolean;
  pre_order_up_avg_odds_min: number;
  pre_order_up_avg_odds_max: number;
  pre_order_down_avg_odds_filter_enabled: boolean;
  pre_order_down_avg_odds_min: number;
  pre_order_down_avg_odds_max: number;
  pre_order_up_min_odds_filter_enabled: boolean;
  pre_order_up_min_odds_min: number;
  pre_order_up_min_odds_max: number;
  pre_order_up_min_pct_filter_enabled: boolean;
  pre_order_up_min_pct_min: number;
  pre_order_up_min_pct_max: number;
  pre_order_down_min_odds_filter_enabled: boolean;
  pre_order_down_min_odds_min: number;
  pre_order_down_min_odds_max: number;
  pre_order_down_max_pct_filter_enabled: boolean;
  pre_order_down_max_pct_min: number;
  pre_order_down_max_pct_max: number;

  open_price_gate_enabled: boolean;
  open_price_override_enabled: boolean;
  open_price_override_within_seconds: number;
  open_price_cushion_enabled: boolean;
  open_price_cushion_min_bps: number;

  prev_windows_check_enabled: boolean;
  prev_windows_count: number;
  prev_windows_min_matches: number;

  pre_decision_avg_odds_enabled: boolean;
  pre_decision_avg_odds_min: number;
  band_dwell_enabled: boolean;
  band_dwell_min_seconds: number;
  band_dwell_require_exceeds_opposite: boolean;
  flip_gate_enabled: boolean;
  flip_gate_max_flips: number;
  flip_gate_skip_seconds: number;
  flip_gate_flip_threshold: number;

  counterpart_override_enabled: boolean;
  counterpart_follow_direction: boolean;
  counterpart_follow_exclusive: boolean;
  counterpart_odds_threshold: number;
  counterpart_decide_within_pct: number;
  counterpart_stable_seconds: number;
  counterpart_require_trend: boolean;
  counterpart_bypass_candle: boolean;
  counterpart_bypass_open_price: boolean;
  counterpart_bypass_prev_windows: boolean;
  counterpart_bypass_kronos: boolean;
  counterpart_bypass_momentum: boolean;
  counterpart_bypass_pre_order: boolean;
  counterpart_bypass_time_ranges: boolean;
  counterpart_bypass_min_odds: boolean;
  counterpart_bypass_max_odds: boolean;

  counterpart_match_order_enabled: boolean;
  counterpart_match_order_require_trend: boolean;
  counterpart_match_order_require_candle: boolean;
  counterpart_match_order_require_open_price: boolean;
  counterpart_match_order_require_prev_windows: boolean;
  counterpart_match_order_require_kronos: boolean;
  counterpart_match_order_require_momentum: boolean;
  counterpart_match_order_require_pre_order: boolean;
  counterpart_match_order_require_time_ranges: boolean;
  counterpart_match_order_require_min_odds: boolean;
  counterpart_match_order_require_max_odds: boolean;

  favorite_hold_enabled: boolean;
  favorite_hold_flip_threshold: number;
  favorite_hold_skip_seconds: number;
  favorite_hold_tier1_enabled: boolean;
  favorite_hold_tier1_min_odds: number;
  favorite_hold_tier1_dwell_seconds: number;
  favorite_hold_tier2_enabled: boolean;
  favorite_hold_tier2_min_rise: number;
  favorite_hold_tier2_check_seconds: number;
  favorite_hold_tier2_require_candle: boolean;
  favorite_hold_tier2_require_open_price: boolean;
  favorite_hold_tier2_require_prev_windows: boolean;
  favorite_hold_tier2_require_kronos: boolean;
  favorite_hold_tier2_require_momentum: boolean;
  favorite_hold_tier2_require_pre_order: boolean;
  favorite_hold_tier2_require_time_ranges: boolean;
  favorite_hold_tier2_require_min_odds: boolean;
  favorite_hold_tier3_enabled: boolean;
  favorite_hold_tier3_bypass_candle: boolean;
  favorite_hold_tier3_bypass_open_price: boolean;
  favorite_hold_tier3_bypass_prev_windows: boolean;
  favorite_hold_tier3_bypass_kronos: boolean;
  favorite_hold_tier3_bypass_momentum: boolean;
  favorite_hold_tier3_bypass_pre_order: boolean;
  favorite_hold_tier3_bypass_time_ranges: boolean;
  favorite_hold_tier3_bypass_min_odds: boolean;

  kronos_check_enabled: boolean;
  kronos_require_direction: boolean;
  kronos_min_confidence: number;
  kronos_model: string;
  kronos_lookback: number;
  kronos_candle_timeframe_seconds: number;
  kronos_sample_count: number;
  kronos_temperature: number;
  kronos_top_p: number;
  kronos_settled_precompute_enabled: boolean;
  kronos_precompute_offset_seconds: number;
  kronos_dynamic_warm_enabled: boolean;
  kronos_dynamic_warm_buffer_seconds: number;
}

type FeatureToggleKey =
  | "candle_filter_enabled"
  | "stop_loss_enabled"
  | "take_profit_enabled"
  | "skip_after_wins_enabled"
  | "cascading_sizes_enabled"
  | "compounding_enabled"
  | "late_buy_enabled"
  | "force_buy_enabled"
  | "trade_time_ranges_enabled"
  | "trade_days_enabled"
  | "odds_momentum_enabled"
  | "momentum_sell_enabled"
  | "pre_order_metrics_enabled"
  | "open_price_gate_enabled"
  | "open_price_override_enabled"
  | "open_price_cushion_enabled"
  | "prev_windows_check_enabled"
  | "pre_decision_avg_odds_enabled"
  | "band_dwell_enabled"
  | "flip_gate_enabled"
  | "peak_drawdown_gate_enabled"
  | "vol_cushion_gate_enabled"
  | "underlying_velocity_gate_enabled"
  | "open_side_gate_enabled"
  | "max_spread_gate_enabled"
  | "counterpart_confirm_gate_enabled"
  | "exit_require_underlying_breach"
  | "counterpart_override_enabled"
  | "favorite_hold_enabled"
  | "kronos_check_enabled";

const FEATURE_TOGGLES: Array<{ key: FeatureToggleKey; title: string; description: string }> = [
  {
    key: "candle_filter_enabled",
    title: "Candle Filter",
    description: "Require recent 1-minute candles to agree with trade direction before entering.",
  },
  {
    key: "stop_loss_enabled",
    title: "Stop-loss",
    description: "Exit early if probability moves against your position before resolution.",
  },
  {
    key: "take_profit_enabled",
    title: "Take-profit",
    description: "Exit early when your side reaches a high enough implied probability.",
  },
  {
    key: "skip_after_wins_enabled",
    title: "Win-streak Skip",
    description: "Pause real entries after a win streak; resume after configured skipped losses.",
  },
  {
    key: "cascading_sizes_enabled",
    title: "Cascading Sizes",
    description: "Use staged trade amounts by streak position instead of a constant trade size.",
  },
  {
    key: "compounding_enabled",
    title: "Compounding Sizing",
    description: "Grow the order size during a win streak by a percent of each win's profit; reset to the base size after any loss.",
  },
  {
    key: "late_buy_enabled",
    title: "Late Buy",
    description: "Bypass candle filter near close and allow entries based on odds gates only.",
  },
  {
    key: "open_price_gate_enabled",
    title: "Open-price Direction Gate",
    description: "Require the current price to be on the picked side of the window's open price before entering (UP: current above open; DOWN: current below open). Uses the feed's own price series, so it works for any market. Cold-start fails closed.",
  },
  {
    key: "open_price_override_enabled",
    title: "Open-price Candle Override",
    description: "When all other checks pass except the candle filter, still enter if the current price is on the correct side of the window open. Trigger right away (within=0) or only in the last X seconds.",
  },
  {
    key: "open_price_cushion_enabled",
    title: "Open-price Cushion Gate",
    description: "Require the current price to be at least N basis points BEYOND the window open in the picked direction (UP: above open; DOWN: below open) before entering. Rejects at-the-money / wrong-side entries where confident-looking odds mask a coin-flip outcome. Threshold is in bps of the open, so BTC and ETH share one setting. Cold-start fails closed.",
  },
  {
    key: "force_buy_enabled",
    title: "Dashboard Manual Buy",
    description: "Allows Buy UP / Buy DOWN actions from the dashboard current market card.",
  },
  {
    key: "trade_time_ranges_enabled",
    title: "Time Schedule",
    description: "Restrict strategy/manual buy and sells to specified local-time ranges.",
  },
  {
    key: "trade_days_enabled",
    title: "Day-of-week Schedule",
    description: "Restrict trading to selected weekdays (evaluated in the display timezone).",
  },
  {
    key: "odds_momentum_enabled",
    title: "Odds Momentum Filter",
    description: "Requires recent odds trend to move in your favor before placing a trade.",
  },
  {
    key: "momentum_sell_enabled",
    title: "Momentum Sell",
    description: "Exit early based on a rolling average of post-fill ask prices. SL sub-mode sells when the average falls below fill ask by a threshold; TP sub-mode sells after the average first peaks above a profit threshold then falls back below it.",
  },
  {
    key: "pre_order_metrics_enabled",
    title: "Pre-order Metrics Filter",
    description: "Track rolling per-window averages + extremes of odds and BTC \u0394% observed prior to fill, and optionally block placements when any metric falls outside its band.",
  },
  {
    key: "prev_windows_check_enabled",
    title: "Previous Windows Direction",
    description: "Require the last N resolved market windows to have closed in the same direction as the picked side before allowing entry (e.g. 2 of last 3 candles matched).",
  },
  {
    key: "pre_decision_avg_odds_enabled",
    title: "Pre-decision Average Odds",
    description: "Require the picked side's average mid odds from window open strictly before configured decision time to meet the minimum. Frozen during monitoring; blocks early entries and missing odds. No counterpart or Favorite Hold bypass.",
  },
  {
    key: "band_dwell_enabled",
    title: "Time in Min \u2194 Max Odds Range Before Decision Time",
    description: "Require the picked side's mid odds to have spent at least N seconds inside the configured min/max odds band before decision time. Filters favourites that only just spiked into the band.",
  },
  {
    key: "flip_gate_enabled",
    title: "Max Favorite Flips",
    description: "Count how many times the picked side's mid odds dropped below the flip threshold (default 50%) since the warm-up skip, and block the entry when that count exceeds the maximum. Hard gate, no bypass.",
  },
  {
    key: "peak_drawdown_gate_enabled",
    title: "Peak Drawdown (Anti Falling-Knife)",
    description: "Block entries where the picked side is falling back INTO the odds band from above: the side's mid must not be more than X below its window peak, and the underlying must still retain at least R of its peak favourable move. Hard gate, no bypass.",
  },
  {
    key: "vol_cushion_gate_enabled",
    title: "Volatility-Normalised Cushion (z-score)",
    description: "Require (current - open) / (realised 1s volatility x sqrt(secs_left)) >= Z in the picked direction, so the cushion scales with how choppy the window is and how much time is left. Hard gate.",
  },
  {
    key: "underlying_velocity_gate_enabled",
    title: "Underlying Velocity (last K seconds)",
    description: "Require the reference price to have moved toward the picked direction over the last K seconds (CLOB odds often lag a fast spot reversal by 1–3s). Hard gate.",
  },
  {
    key: "open_side_gate_enabled",
    title: "Open-Side Streak / Open Crossings",
    description: "Require the reference price to have stayed continuously on the picked side of the window open for N seconds and/or crossed the open at most K times (filters pinning coin-flip windows). Hard gate.",
  },
  {
    key: "max_spread_gate_enabled",
    title: "Bid-Ask Spread Cap",
    description: "Require (ask - bid) on the picked side to be within the cap at entry so you never pay far above mid into a market-maker-widened book. Hard gate.",
  },
  {
    key: "counterpart_confirm_gate_enabled",
    title: "Counterpart Confirmation Gate",
    description: "Filter (not override): block the entry when the time-aligned counterpart market (BTC \u2194 ETH) disagrees — by underlying move from its open, or by favourite side. Fails closed when the counterpart window is missing.",
  },
  {
    key: "exit_require_underlying_breach",
    title: "Underlying-Confirmed Protective Exits (Smart SL)",
    description: "Only let the odds Stop Loss and Momentum Sell SL fire once the reference price has actually given back its cushion from the window open. Prevents panic-selling winners during brief CLOB quote pulls. Take-profit is unaffected.",
  },
  {
    key: "kronos_check_enabled",
    title: "Kronos Forecast",
    description: "Use the Kronos foundation model's window forecast as an entry gate: require the predicted close direction to match the picked side and the predicted probability to clear a confidence floor. Requires the kronos-service running and forecasts precomputed (see README).",
  },
  {
    key: "counterpart_override_enabled",
    title: "Counterpart Market Override",
    description: COUNTERPART_FIELD_DESCRIPTIONS.counterpart_override_enabled!,
  },
  {
    key: "favorite_hold_enabled",
    title: "Favorite Hold (3-Tier)",
    description: FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_enabled!,
  },
];

const DAY_TOKENS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as const;
const DAY_ALIASES: Record<string, string> = {
  MON: "MON", MONDAY: "MON",
  TUE: "TUE", TUES: "TUE", TUESDAY: "TUE",
  WED: "WED", WEDS: "WED", WEDNESDAY: "WED",
  THU: "THU", THUR: "THU", THURS: "THU", THURSDAY: "THU",
  FRI: "FRI", FRIDAY: "FRI",
  SAT: "SAT", SATURDAY: "SAT",
  SUN: "SUN", SUNDAY: "SUN",
};

export function parseTradeDays(raw: string): string[] {
  const out = new Set<string>();
  for (const piece of (raw ?? "").split(",")) {
    const token = piece.trim().toUpperCase();
    if (!token) continue;
    const norm = DAY_ALIASES[token];
    if (norm) out.add(norm);
  }
  return DAY_TOKENS.filter((t) => out.has(t));
}

function formatDecisionTime(seconds: number): string {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function parseDecisionTime(value: string): number {
  const [m, s] = value.trim().split(":");
  const min = Number(m);
  const sec = Number(s);
  if (!Number.isFinite(min) || !Number.isFinite(sec)) return 240;
  // Reject negative components and out-of-range seconds (>= 60): fall
  // back to the 240s default instead of silently producing a bogus value.
  if (min < 0 || sec < 0 || sec >= 60) return 240;
  return Math.max(0, Math.floor(min) * 60 + Math.floor(sec));
}

export function fromConfig(data: Record<string, unknown>): FormState {
  return {
    config_name: String(data.config_name ?? ""),
    trade_size_usdc: String(data.trade_size_usdc ?? "1"),
    min_odds: String(data.min_odds ?? "0.50"),
    max_odds: String(data.max_odds ?? "0.60"),
    no_buy_window_seconds: Number(data.no_buy_window_seconds ?? 0),
    no_sell_window_seconds: Number(data.no_sell_window_seconds ?? 0),
    decision_time: formatDecisionTime(Number(data.decision_time_seconds ?? 240)),
    // Real engine field since the candle gate became its own toggle. Older
    // snapshots have no field: infer it from min_matching_candles so the
    // form shows the gate exactly as the engine (default true) enforces it.
    candle_filter_enabled: data.candle_filter_enabled == null
      ? Number(data.min_matching_candles ?? 3) > 0
      : Boolean(data.candle_filter_enabled),
    min_matching_candles: Number(data.min_matching_candles ?? 3),
    candles_to_check: Number(data.candles_to_check ?? 4),
    allow_live_candle: Boolean(data.allow_live_candle ?? false),
    live_candle_minute: Number(data.live_candle_minute ?? 0),
    candle_timeframe_seconds: Number(data.candle_timeframe_seconds ?? 0),
    skip_first_candles: Number(data.skip_first_candles ?? 0),

    order_type: String(data.order_type ?? "FOK"),
    slippage_ticks: Number(data.slippage_ticks ?? 1),
    gtc_auto_cancel_on_drift: Boolean(data.gtc_auto_cancel_on_drift ?? true),
    gtc_recheck_seconds: Number(data.gtc_recheck_seconds ?? 2),
    gtc_cancel_unfilled_after_seconds: Number(data.gtc_cancel_unfilled_after_seconds ?? 15),
    allow_multiple_orders_per_window: Boolean(data.allow_multiple_orders_per_window ?? false),
    entry_confirm_seconds: Math.max(0, Math.trunc(Number(data.entry_confirm_seconds ?? 0) || 0)),
    sim_execution_delay_seconds: Math.max(0, Math.trunc(Number(data.sim_execution_delay_seconds ?? 0) || 0)),
    take_profit_order_type: String(data.take_profit_order_type ?? "REACTIVE").toUpperCase(),
    exit_order_type: String(data.exit_order_type ?? "INHERIT").toUpperCase(),
    exit_slippage_ticks: Math.max(-1, Math.trunc(Number(data.exit_slippage_ticks ?? -1) || 0)),
    exit_require_underlying_breach: Boolean(data.exit_require_underlying_breach ?? false),
    exit_underlying_breach_bps: Number(data.exit_underlying_breach_bps ?? 0) || 0,
    peak_drawdown_gate_enabled: Boolean(data.peak_drawdown_gate_enabled ?? false),
    max_drop_from_peak_odds: Math.max(0, Number(data.max_drop_from_peak_odds ?? 0.06) || 0),
    min_price_excursion_retention: Math.min(1, Math.max(0, Number(data.min_price_excursion_retention ?? 0) || 0)),
    vol_cushion_gate_enabled: Boolean(data.vol_cushion_gate_enabled ?? false),
    vol_cushion_min_z: Math.max(0, Number(data.vol_cushion_min_z ?? 1.5) || 0),
    underlying_velocity_gate_enabled: Boolean(data.underlying_velocity_gate_enabled ?? false),
    underlying_velocity_lookback_seconds: Math.max(1, Math.trunc(Number(data.underlying_velocity_lookback_seconds ?? 15) || 15)),
    underlying_velocity_min_bps: Number(data.underlying_velocity_min_bps ?? 0) || 0,
    open_side_gate_enabled: Boolean(data.open_side_gate_enabled ?? false),
    open_side_streak_seconds: Math.max(0, Math.trunc(Number(data.open_side_streak_seconds ?? 60) || 0)),
    max_open_crosses: Math.max(0, Math.trunc(Number(data.max_open_crosses ?? 0) || 0)),
    max_spread_gate_enabled: Boolean(data.max_spread_gate_enabled ?? false),
    max_spread: Math.max(0, Number(data.max_spread ?? 0.03) || 0),
    counterpart_confirm_gate_enabled: Boolean(data.counterpart_confirm_gate_enabled ?? false),
    counterpart_confirm_mode: String(data.counterpart_confirm_mode ?? "price").toLowerCase() === "favorite" ? "favorite" : "price",
    counterpart_confirm_max_opposing_bps: Math.max(0, Number(data.counterpart_confirm_max_opposing_bps ?? 0) || 0),
    kronos_min_edge_over_ask: Math.min(1, Math.max(0, Number(data.kronos_min_edge_over_ask ?? 0) || 0)),
    fee_rate: String(data.fee_rate ?? "0.076"),
    fee_exponent: Number(data.fee_exponent ?? 1.0),

    stop_loss_enabled: Boolean(data.stop_loss_enabled ?? false),
    stop_loss_threshold: String(data.stop_loss_threshold ?? "0.4"),
    stop_loss_trigger_within_seconds: Number(data.stop_loss_trigger_within_seconds ?? 3),
    stop_loss_btc_delta_enabled: Boolean(data.stop_loss_btc_delta_enabled ?? false),
    stop_loss_btc_delta_dollars: String(data.stop_loss_btc_delta_dollars ?? "1"),
    stop_loss_btc_poll_interval_seconds: Number(data.stop_loss_btc_poll_interval_seconds ?? 1),
    stop_loss_btc_delta_mode: String(data.stop_loss_btc_delta_mode ?? "trigger"),
    stop_loss_btc_confirm_seconds: Number(data.stop_loss_btc_confirm_seconds ?? 0),
    stop_loss_last_candle_instant: Boolean(data.stop_loss_last_candle_instant ?? false),
    stop_loss_last_candle_seconds: Number(data.stop_loss_last_candle_seconds ?? 5),

    take_profit_enabled: Boolean(data.take_profit_enabled ?? false),
    take_profit_threshold: String(data.take_profit_threshold ?? "0.9"),

    skip_after_wins_enabled: Boolean(data.skip_after_wins_enabled ?? false),
    skip_after_wins_threshold: Number(data.skip_after_wins_threshold ?? 7),
    skip_after_wins_losses_to_resume: Number(data.skip_after_wins_losses_to_resume ?? 1),

    cascading_sizes_enabled: Boolean(data.cascading_sizes_enabled ?? false),
    cascading_sizes: String(data.cascading_sizes ?? "10.0,7.0,5.0"),

    compounding_enabled: Boolean(data.compounding_enabled ?? false),
    compounding_win_pct: Number(data.compounding_win_pct ?? 0),
    compounding_loss_pct: Number(data.compounding_loss_pct ?? 0),
    compounding_max_size_usdc: Number(data.compounding_max_size_usdc ?? 0),
    compounding_stop_after_wins: Number(data.compounding_stop_after_wins ?? data.compounding_reset_after_wins ?? 0),
    compounding_stop_size_stay: Boolean(data.compounding_stop_size_stay ?? false),
    compounding_resume_from_saved: Boolean(data.compounding_resume_from_saved ?? false),

    late_buy_enabled: Boolean(data.late_buy_enabled ?? false),
    late_buy_within_seconds: Number(data.late_buy_within_seconds ?? 7),
    late_buy_trade_size_usdc: String(data.late_buy_trade_size_usdc ?? "10"),

    force_buy_enabled: Boolean(data.force_buy_enabled ?? true),

    trade_time_ranges_enabled: Boolean(data.trade_time_ranges_enabled ?? true),
    trade_time_ranges: String(data.trade_time_ranges ?? "00:00-23:59"),

    trade_days_enabled: Boolean(data.trade_days_enabled ?? false),
    trade_days_allowed: String(data.trade_days_allowed ?? "MON,TUE,WED,THU,FRI,SAT,SUN"),

    dry_run: Boolean(data.dry_run ?? false),
    log_level: String(data.log_level ?? "INFO"),
    log_file: String(data.log_file ?? "bot.log"),
    state_file: String(data.state_file ?? "bot_state.json"),
    display_timezone: String(data.display_timezone ?? "America/New_York"),
    price_feed: String(data.price_feed ?? "polymarket_chainlink"),

    odds_momentum_enabled: Boolean(data.odds_momentum_enabled ?? true),
    odds_momentum_lookback: Number(data.odds_momentum_lookback ?? 8),
    odds_momentum_required_increasing: Number(data.odds_momentum_required_increasing ?? 8),
    odds_momentum_ignore_last_seconds: Number(data.odds_momentum_ignore_last_seconds ?? 3),
    odds_momentum_use_unique_odds: Boolean(data.odds_momentum_use_unique_odds ?? true),
    odds_momentum_avg_lookback: Number(data.odds_momentum_avg_lookback ?? 0),
    odds_momentum_avg_max_diff: Number(data.odds_momentum_avg_max_diff ?? 0.5),

    momentum_sell_enabled: Boolean(data.momentum_sell_enabled ?? false),
    momentum_sell_window_type: String(data.momentum_sell_window_type ?? "number"),
    momentum_sell_window_value: Number(data.momentum_sell_window_value ?? 10),
    momentum_sell_sl_enabled: Boolean(data.momentum_sell_sl_enabled ?? true),
    momentum_sell_sl_threshold_type: String(data.momentum_sell_sl_threshold_type ?? "points"),
    momentum_sell_sl_threshold_value: Number(data.momentum_sell_sl_threshold_value ?? 0.05),
    momentum_sell_sl_require_seconds: Number(data.momentum_sell_sl_require_seconds ?? 0),
    momentum_sell_sl_start_at_seconds_left: Number(data.momentum_sell_sl_start_at_seconds_left ?? 0),
    momentum_sell_tp_enabled: Boolean(data.momentum_sell_tp_enabled ?? false),
    momentum_sell_tp_threshold_type: String(data.momentum_sell_tp_threshold_type ?? "points"),
    momentum_sell_tp_threshold_value: Number(data.momentum_sell_tp_threshold_value ?? 0.05),
    momentum_sell_tp_min_seconds_below: Number(data.momentum_sell_tp_min_seconds_below ?? 3),
    momentum_sell_tp_start_at_seconds_left: Number(data.momentum_sell_tp_start_at_seconds_left ?? 0),
    momentum_sell_profit_is_win: Boolean(data.momentum_sell_profit_is_win ?? true),
    momentum_sell_loss_breaks_streak: Boolean(data.momentum_sell_loss_breaks_streak ?? true),
    loss_streak_continue_threshold_usdc: Number(data.loss_streak_continue_threshold_usdc ?? 0),

    pre_order_metrics_enabled: Boolean(data.pre_order_metrics_enabled ?? false),
    pre_order_up_avg_odds_filter_enabled: Boolean(data.pre_order_up_avg_odds_filter_enabled ?? false),
    pre_order_up_avg_odds_min: Number(data.pre_order_up_avg_odds_min ?? 0),
    pre_order_up_avg_odds_max: Number(data.pre_order_up_avg_odds_max ?? 1),
    pre_order_down_avg_odds_filter_enabled: Boolean(data.pre_order_down_avg_odds_filter_enabled ?? false),
    pre_order_down_avg_odds_min: Number(data.pre_order_down_avg_odds_min ?? 0),
    pre_order_down_avg_odds_max: Number(data.pre_order_down_avg_odds_max ?? 1),
    pre_order_up_min_odds_filter_enabled: Boolean(data.pre_order_up_min_odds_filter_enabled ?? false),
    pre_order_up_min_odds_min: Number(data.pre_order_up_min_odds_min ?? 0),
    pre_order_up_min_odds_max: Number(data.pre_order_up_min_odds_max ?? 1),
    pre_order_up_min_pct_filter_enabled: Boolean(data.pre_order_up_min_pct_filter_enabled ?? false),
    pre_order_up_min_pct_min: Number(data.pre_order_up_min_pct_min ?? -100),
    pre_order_up_min_pct_max: Number(data.pre_order_up_min_pct_max ?? 100),
    pre_order_down_min_odds_filter_enabled: Boolean(data.pre_order_down_min_odds_filter_enabled ?? false),
    pre_order_down_min_odds_min: Number(data.pre_order_down_min_odds_min ?? 0),
    pre_order_down_min_odds_max: Number(data.pre_order_down_min_odds_max ?? 1),
    pre_order_down_max_pct_filter_enabled: Boolean(data.pre_order_down_max_pct_filter_enabled ?? false),
    pre_order_down_max_pct_min: Number(data.pre_order_down_max_pct_min ?? -100),
    pre_order_down_max_pct_max: Number(data.pre_order_down_max_pct_max ?? 100),

    open_price_gate_enabled: Boolean(data.open_price_gate_enabled ?? false),
    open_price_override_enabled: Boolean(data.open_price_override_enabled ?? false),
    open_price_override_within_seconds: Math.max(0, Number(data.open_price_override_within_seconds ?? 0)),
    open_price_cushion_enabled: Boolean(data.open_price_cushion_enabled ?? false),
    open_price_cushion_min_bps: Math.max(0, Number(data.open_price_cushion_min_bps ?? 2.0)),

    prev_windows_check_enabled: Boolean(data.prev_windows_check_enabled ?? false),
    prev_windows_count: Math.max(1, Number(data.prev_windows_count ?? 3)),
    prev_windows_min_matches: Math.max(0, Number(data.prev_windows_min_matches ?? 2)),

    pre_decision_avg_odds_enabled: Boolean(data.pre_decision_avg_odds_enabled ?? false),
    pre_decision_avg_odds_min: Number(data.pre_decision_avg_odds_min ?? 0.53),
    band_dwell_enabled: Boolean(data.band_dwell_enabled ?? false),
    band_dwell_min_seconds: Math.max(0, Number(data.band_dwell_min_seconds ?? 0)),
    band_dwell_require_exceeds_opposite: Boolean(data.band_dwell_require_exceeds_opposite ?? false),
    flip_gate_enabled: Boolean(data.flip_gate_enabled ?? false),
    flip_gate_max_flips: Math.max(0, Math.trunc(Number(data.flip_gate_max_flips ?? 0) || 0)),
    flip_gate_skip_seconds: Math.max(0, Math.trunc(Number(data.flip_gate_skip_seconds ?? 0) || 0)),
    flip_gate_flip_threshold: Math.min(1, Math.max(0, Number(data.flip_gate_flip_threshold ?? 0.5) || 0)),

    counterpart_override_enabled: Boolean(data.counterpart_override_enabled ?? false),
    counterpart_follow_direction: Boolean(data.counterpart_follow_direction ?? false),
    counterpart_follow_exclusive: Boolean(data.counterpart_follow_exclusive ?? false),
    counterpart_odds_threshold: Math.min(1, Math.max(0, Number(data.counterpart_odds_threshold ?? 0.95))),
    counterpart_decide_within_pct: Math.min(100, Math.max(0, Number(data.counterpart_decide_within_pct ?? 0))),
    counterpart_stable_seconds: Math.max(0, Number(data.counterpart_stable_seconds ?? 0)),
    counterpart_require_trend: Boolean(data.counterpart_require_trend ?? true),
    counterpart_bypass_candle: Boolean(data.counterpart_bypass_candle ?? true),
    counterpart_bypass_open_price: Boolean(data.counterpart_bypass_open_price ?? false),
    counterpart_bypass_prev_windows: Boolean(data.counterpart_bypass_prev_windows ?? false),
    counterpart_bypass_kronos: Boolean(data.counterpart_bypass_kronos ?? false),
    counterpart_bypass_momentum: Boolean(data.counterpart_bypass_momentum ?? false),
    counterpart_bypass_pre_order: Boolean(data.counterpart_bypass_pre_order ?? false),
    counterpart_bypass_time_ranges: Boolean(data.counterpart_bypass_time_ranges ?? false),
    counterpart_bypass_min_odds: Boolean(data.counterpart_bypass_min_odds ?? false),
    counterpart_bypass_max_odds: Boolean(data.counterpart_bypass_max_odds ?? false),

    counterpart_match_order_enabled: Boolean(data.counterpart_match_order_enabled ?? false),
    counterpart_match_order_require_trend: Boolean(data.counterpart_match_order_require_trend ?? false),
    counterpart_match_order_require_candle: Boolean(data.counterpart_match_order_require_candle ?? false),
    counterpart_match_order_require_open_price: Boolean(data.counterpart_match_order_require_open_price ?? false),
    counterpart_match_order_require_prev_windows: Boolean(data.counterpart_match_order_require_prev_windows ?? false),
    counterpart_match_order_require_kronos: Boolean(data.counterpart_match_order_require_kronos ?? false),
    counterpart_match_order_require_momentum: Boolean(data.counterpart_match_order_require_momentum ?? false),
    counterpart_match_order_require_pre_order: Boolean(data.counterpart_match_order_require_pre_order ?? false),
    counterpart_match_order_require_time_ranges: Boolean(data.counterpart_match_order_require_time_ranges ?? false),
    counterpart_match_order_require_min_odds: Boolean(data.counterpart_match_order_require_min_odds ?? false),
    counterpart_match_order_require_max_odds: Boolean(data.counterpart_match_order_require_max_odds ?? false),

    favorite_hold_enabled: Boolean(data.favorite_hold_enabled ?? false),
    favorite_hold_flip_threshold: Math.min(1, Math.max(0, Number(data.favorite_hold_flip_threshold ?? 0.51))),
    favorite_hold_skip_seconds: Math.max(0, Number(data.favorite_hold_skip_seconds ?? 0)),
    favorite_hold_tier1_enabled: Boolean(data.favorite_hold_tier1_enabled ?? false),
    favorite_hold_tier1_min_odds: Math.min(1, Math.max(0, Number(data.favorite_hold_tier1_min_odds ?? 0.8))),
    favorite_hold_tier1_dwell_seconds: Math.max(0, Number(data.favorite_hold_tier1_dwell_seconds ?? 60)),
    favorite_hold_tier2_enabled: Boolean(data.favorite_hold_tier2_enabled ?? false),
    favorite_hold_tier2_min_rise: Math.min(1, Math.max(0, Number(data.favorite_hold_tier2_min_rise ?? 0.1))),
    favorite_hold_tier2_check_seconds: Math.max(0, Number(data.favorite_hold_tier2_check_seconds ?? 120)),
    favorite_hold_tier2_require_candle: Boolean(data.favorite_hold_tier2_require_candle ?? false),
    favorite_hold_tier2_require_open_price: Boolean(data.favorite_hold_tier2_require_open_price ?? false),
    favorite_hold_tier2_require_prev_windows: Boolean(data.favorite_hold_tier2_require_prev_windows ?? false),
    favorite_hold_tier2_require_kronos: Boolean(data.favorite_hold_tier2_require_kronos ?? false),
    favorite_hold_tier2_require_momentum: Boolean(data.favorite_hold_tier2_require_momentum ?? false),
    favorite_hold_tier2_require_pre_order: Boolean(data.favorite_hold_tier2_require_pre_order ?? false),
    favorite_hold_tier2_require_time_ranges: Boolean(data.favorite_hold_tier2_require_time_ranges ?? false),
    favorite_hold_tier2_require_min_odds: Boolean(data.favorite_hold_tier2_require_min_odds ?? false),
    favorite_hold_tier3_enabled: Boolean(data.favorite_hold_tier3_enabled ?? false),
    favorite_hold_tier3_bypass_candle: Boolean(data.favorite_hold_tier3_bypass_candle ?? false),
    favorite_hold_tier3_bypass_open_price: Boolean(data.favorite_hold_tier3_bypass_open_price ?? false),
    favorite_hold_tier3_bypass_prev_windows: Boolean(data.favorite_hold_tier3_bypass_prev_windows ?? false),
    favorite_hold_tier3_bypass_kronos: Boolean(data.favorite_hold_tier3_bypass_kronos ?? false),
    favorite_hold_tier3_bypass_momentum: Boolean(data.favorite_hold_tier3_bypass_momentum ?? false),
    favorite_hold_tier3_bypass_pre_order: Boolean(data.favorite_hold_tier3_bypass_pre_order ?? false),
    favorite_hold_tier3_bypass_time_ranges: Boolean(data.favorite_hold_tier3_bypass_time_ranges ?? false),
    favorite_hold_tier3_bypass_min_odds: Boolean(data.favorite_hold_tier3_bypass_min_odds ?? false),

    kronos_check_enabled: Boolean(data.kronos_check_enabled ?? false),
    kronos_require_direction: Boolean(data.kronos_require_direction ?? true),
    kronos_min_confidence: Math.min(1, Math.max(0, Number(data.kronos_min_confidence ?? 0.5))),
    kronos_model: String(data.kronos_model ?? "small"),
    kronos_lookback: Math.max(1, Number(data.kronos_lookback ?? 400)),
    kronos_candle_timeframe_seconds: Math.max(1, Number(data.kronos_candle_timeframe_seconds ?? 60)),
    kronos_sample_count: Math.max(1, Number(data.kronos_sample_count ?? 5)),
    kronos_temperature: Math.max(0.01, Number(data.kronos_temperature ?? 1.0)),
    kronos_top_p: Math.min(1, Math.max(0.01, Number(data.kronos_top_p ?? 0.9))),
    kronos_settled_precompute_enabled: Boolean(data.kronos_settled_precompute_enabled ?? false),
    kronos_precompute_offset_seconds: Math.max(0, Number(data.kronos_precompute_offset_seconds ?? 0)),
    kronos_dynamic_warm_enabled: Boolean(data.kronos_dynamic_warm_enabled ?? false),
    kronos_dynamic_warm_buffer_seconds: Math.max(0, Number(data.kronos_dynamic_warm_buffer_seconds ?? 12)),
  };
}

function toConfig(s: FormState): Record<string, unknown> {
  return {
    config_name: s.config_name,
    trade_size_usdc: s.trade_size_usdc,
    min_odds: s.min_odds,
    max_odds: s.max_odds,
    // The gate is now a real engine toggle: persist it and keep the candle
    // parameters intact so re-enabling restores the operator's values
    // (the engines treat candle_filter_enabled=false as min_matching=0).
    candle_filter_enabled: s.candle_filter_enabled,
    min_matching_candles: s.min_matching_candles,
    candles_to_check: s.candles_to_check,
    allow_live_candle: s.allow_live_candle,
    live_candle_minute: s.allow_live_candle ? s.live_candle_minute : 0,
    candle_timeframe_seconds: Math.max(0, Math.floor(s.candle_timeframe_seconds)),
    skip_first_candles: Math.max(0, Math.floor(s.skip_first_candles)),
    decision_time_seconds: parseDecisionTime(s.decision_time),
    monitor_after_decision: true,
    monitor_interval_seconds: 1,
    order_type: s.order_type,
    slippage_ticks: s.slippage_ticks,
    gtc_auto_cancel_on_drift: s.gtc_auto_cancel_on_drift,
    gtc_recheck_seconds: s.gtc_recheck_seconds,
    gtc_cancel_unfilled_after_seconds: s.gtc_cancel_unfilled_after_seconds,
    allow_multiple_orders_per_window: s.allow_multiple_orders_per_window,
    entry_confirm_seconds: s.entry_confirm_seconds,
    sim_execution_delay_seconds: s.sim_execution_delay_seconds,
    take_profit_order_type: s.take_profit_order_type,
    exit_order_type: s.exit_order_type,
    exit_slippage_ticks: s.exit_slippage_ticks,
    exit_require_underlying_breach: s.exit_require_underlying_breach,
    exit_underlying_breach_bps: s.exit_underlying_breach_bps,
    peak_drawdown_gate_enabled: s.peak_drawdown_gate_enabled,
    max_drop_from_peak_odds: s.max_drop_from_peak_odds,
    min_price_excursion_retention: s.min_price_excursion_retention,
    vol_cushion_gate_enabled: s.vol_cushion_gate_enabled,
    vol_cushion_min_z: s.vol_cushion_min_z,
    underlying_velocity_gate_enabled: s.underlying_velocity_gate_enabled,
    underlying_velocity_lookback_seconds: s.underlying_velocity_lookback_seconds,
    underlying_velocity_min_bps: s.underlying_velocity_min_bps,
    open_side_gate_enabled: s.open_side_gate_enabled,
    open_side_streak_seconds: s.open_side_streak_seconds,
    max_open_crosses: s.max_open_crosses,
    max_spread_gate_enabled: s.max_spread_gate_enabled,
    max_spread: s.max_spread,
    counterpart_confirm_gate_enabled: s.counterpart_confirm_gate_enabled,
    counterpart_confirm_mode: s.counterpart_confirm_mode,
    counterpart_confirm_max_opposing_bps: s.counterpart_confirm_max_opposing_bps,
    kronos_min_edge_over_ask: s.kronos_min_edge_over_ask,
    fee_rate: s.fee_rate,
    fee_exponent: s.fee_exponent,
    no_buy_window_seconds: s.no_buy_window_seconds,
    no_sell_window_seconds: s.no_sell_window_seconds,

    stop_loss_enabled: s.stop_loss_enabled,
    stop_loss_threshold: s.stop_loss_threshold,
    stop_loss_trigger_within_seconds: s.stop_loss_trigger_within_seconds,
    stop_loss_btc_delta_enabled: s.stop_loss_btc_delta_enabled,
    stop_loss_btc_delta_dollars: s.stop_loss_btc_delta_dollars,
    stop_loss_btc_poll_interval_seconds: s.stop_loss_btc_poll_interval_seconds,
    stop_loss_btc_delta_mode: s.stop_loss_btc_delta_mode,
    stop_loss_btc_confirm_seconds: s.stop_loss_btc_confirm_seconds,
    stop_loss_last_candle_instant: s.stop_loss_last_candle_instant,
    stop_loss_last_candle_seconds: s.stop_loss_last_candle_seconds,

    take_profit_enabled: s.take_profit_enabled,
    take_profit_threshold: s.take_profit_threshold,

    skip_after_wins_enabled: s.skip_after_wins_enabled,
    skip_after_wins_threshold: s.skip_after_wins_threshold,
    skip_after_wins_losses_to_resume: s.skip_after_wins_losses_to_resume,

    cascading_sizes_enabled: s.cascading_sizes_enabled,
    cascading_sizes: s.cascading_sizes,

    compounding_enabled: s.compounding_enabled,
    compounding_win_pct: s.compounding_win_pct,
    // Obsolete under streak-reset compounding: any loss fully resets the
    // accumulated delta, so a loss percentage no longer applies.
    compounding_loss_pct: 0,
    compounding_max_size_usdc: s.compounding_max_size_usdc,
    compounding_stop_after_wins: s.compounding_stop_after_wins,
    compounding_stop_size_stay: s.compounding_stop_size_stay,
    compounding_resume_from_saved: s.compounding_resume_from_saved,

    late_buy_enabled: s.late_buy_enabled,
    late_buy_within_seconds: s.late_buy_within_seconds,
    late_buy_trade_size_usdc: s.late_buy_trade_size_usdc,

    force_buy_enabled: s.force_buy_enabled,

    trade_time_ranges_enabled: s.trade_time_ranges_enabled,
    trade_time_ranges: s.trade_time_ranges,

    trade_days_enabled: s.trade_days_enabled,
    trade_days_allowed: s.trade_days_allowed,

    dry_run: s.dry_run,
    log_level: s.log_level,
    log_file: s.log_file,
    state_file: s.state_file,
    display_timezone: s.display_timezone,
    price_feed: s.price_feed,

    odds_momentum_enabled: s.odds_momentum_enabled,
    odds_momentum_lookback: s.odds_momentum_lookback,
    odds_momentum_required_increasing: s.odds_momentum_required_increasing,
    odds_momentum_ignore_last_seconds: s.odds_momentum_ignore_last_seconds,
    odds_momentum_use_unique_odds: s.odds_momentum_use_unique_odds,
    odds_momentum_avg_lookback: s.odds_momentum_avg_lookback,
    odds_momentum_avg_max_diff: s.odds_momentum_avg_max_diff,

    momentum_sell_enabled: s.momentum_sell_enabled,
    momentum_sell_window_type: s.momentum_sell_window_type,
    momentum_sell_window_value: s.momentum_sell_window_value,
    momentum_sell_sl_enabled: s.momentum_sell_sl_enabled,
    momentum_sell_sl_threshold_type: s.momentum_sell_sl_threshold_type,
    momentum_sell_sl_threshold_value: s.momentum_sell_sl_threshold_value,
    momentum_sell_sl_require_seconds: s.momentum_sell_sl_require_seconds,
    momentum_sell_sl_start_at_seconds_left: s.momentum_sell_sl_start_at_seconds_left,
    momentum_sell_tp_enabled: s.momentum_sell_tp_enabled,
    momentum_sell_tp_threshold_type: s.momentum_sell_tp_threshold_type,
    momentum_sell_tp_threshold_value: s.momentum_sell_tp_threshold_value,
    momentum_sell_tp_min_seconds_below: s.momentum_sell_tp_min_seconds_below,
    momentum_sell_tp_start_at_seconds_left: s.momentum_sell_tp_start_at_seconds_left,
    momentum_sell_profit_is_win: s.momentum_sell_profit_is_win,
    momentum_sell_loss_breaks_streak: s.momentum_sell_loss_breaks_streak,
    loss_streak_continue_threshold_usdc: s.loss_streak_continue_threshold_usdc,

    pre_order_metrics_enabled: s.pre_order_metrics_enabled,
    pre_order_up_avg_odds_filter_enabled: s.pre_order_metrics_enabled && s.pre_order_up_avg_odds_filter_enabled,
    pre_order_up_avg_odds_min: s.pre_order_up_avg_odds_min,
    pre_order_up_avg_odds_max: s.pre_order_up_avg_odds_max,
    pre_order_down_avg_odds_filter_enabled: s.pre_order_metrics_enabled && s.pre_order_down_avg_odds_filter_enabled,
    pre_order_down_avg_odds_min: s.pre_order_down_avg_odds_min,
    pre_order_down_avg_odds_max: s.pre_order_down_avg_odds_max,
    pre_order_up_min_odds_filter_enabled: s.pre_order_metrics_enabled && s.pre_order_up_min_odds_filter_enabled,
    pre_order_up_min_odds_min: s.pre_order_up_min_odds_min,
    pre_order_up_min_odds_max: s.pre_order_up_min_odds_max,
    pre_order_up_min_pct_filter_enabled: s.pre_order_metrics_enabled && s.pre_order_up_min_pct_filter_enabled,
    pre_order_up_min_pct_min: s.pre_order_up_min_pct_min,
    pre_order_up_min_pct_max: s.pre_order_up_min_pct_max,
    pre_order_down_min_odds_filter_enabled: s.pre_order_metrics_enabled && s.pre_order_down_min_odds_filter_enabled,
    pre_order_down_min_odds_min: s.pre_order_down_min_odds_min,
    pre_order_down_min_odds_max: s.pre_order_down_min_odds_max,
    pre_order_down_max_pct_filter_enabled: s.pre_order_metrics_enabled && s.pre_order_down_max_pct_filter_enabled,
    pre_order_down_max_pct_min: s.pre_order_down_max_pct_min,
    pre_order_down_max_pct_max: s.pre_order_down_max_pct_max,

    open_price_gate_enabled: s.open_price_gate_enabled,
    open_price_override_enabled: s.open_price_override_enabled,
    open_price_override_within_seconds: Math.max(0, Math.floor(s.open_price_override_within_seconds)),
    open_price_cushion_enabled: s.open_price_cushion_enabled,
    open_price_cushion_min_bps: Math.max(0, s.open_price_cushion_min_bps),

    prev_windows_check_enabled: s.prev_windows_check_enabled,
    prev_windows_count: Math.max(1, Math.floor(s.prev_windows_count)),
    prev_windows_min_matches: s.prev_windows_check_enabled
      ? Math.min(
          Math.max(0, Math.floor(s.prev_windows_min_matches)),
          Math.max(1, Math.floor(s.prev_windows_count)),
        )
      : Math.max(0, Math.floor(s.prev_windows_min_matches)),

    // Band-dwell gate. Always serialised (passthrough config) so the
    // round-trip is lossless whether or not the gate is enabled.
    pre_decision_avg_odds_enabled: s.pre_decision_avg_odds_enabled,
    pre_decision_avg_odds_min: s.pre_decision_avg_odds_min,
    band_dwell_enabled: s.band_dwell_enabled,
    band_dwell_min_seconds: Math.max(0, Math.floor(s.band_dwell_min_seconds)),
    band_dwell_require_exceeds_opposite: s.band_dwell_require_exceeds_opposite,
    flip_gate_enabled: s.flip_gate_enabled,
    flip_gate_max_flips: Math.max(0, Math.floor(s.flip_gate_max_flips)),
    flip_gate_skip_seconds: Math.max(0, Math.floor(s.flip_gate_skip_seconds)),
    flip_gate_flip_threshold: s.flip_gate_flip_threshold,

    // Counterpart-market override (BTC <-> ETH, same timeframe). The
    // bypass toggles + params are only meaningful when enabled, but we
    // always serialise them (passthrough config) so the round-trip is
    // lossless. See trading_bot.config.
    counterpart_override_enabled: s.counterpart_override_enabled,
    counterpart_follow_direction: s.counterpart_follow_direction,
    counterpart_follow_exclusive: s.counterpart_follow_exclusive,
    counterpart_odds_threshold: Math.min(1, Math.max(0, s.counterpart_odds_threshold)),
    counterpart_decide_within_pct: Math.min(100, Math.max(0, s.counterpart_decide_within_pct)),
    counterpart_stable_seconds: Math.max(0, Math.floor(s.counterpart_stable_seconds)),
    counterpart_require_trend: s.counterpart_require_trend,
    counterpart_bypass_candle: s.counterpart_bypass_candle,
    counterpart_bypass_open_price: s.counterpart_bypass_open_price,
    counterpart_bypass_prev_windows: s.counterpart_bypass_prev_windows,
    counterpart_bypass_kronos: s.counterpart_bypass_kronos,
    counterpart_bypass_momentum: s.counterpart_bypass_momentum,
    counterpart_bypass_pre_order: s.counterpart_bypass_pre_order,
    counterpart_bypass_time_ranges: s.counterpart_bypass_time_ranges,
    counterpart_bypass_min_odds: s.counterpart_bypass_min_odds,
    counterpart_bypass_max_odds: s.counterpart_bypass_max_odds,

    counterpart_match_order_enabled: s.counterpart_match_order_enabled,
    counterpart_match_order_require_trend: s.counterpart_match_order_require_trend,
    counterpart_match_order_require_candle: s.counterpart_match_order_require_candle,
    counterpart_match_order_require_open_price: s.counterpart_match_order_require_open_price,
    counterpart_match_order_require_prev_windows: s.counterpart_match_order_require_prev_windows,
    counterpart_match_order_require_kronos: s.counterpart_match_order_require_kronos,
    counterpart_match_order_require_momentum: s.counterpart_match_order_require_momentum,
    counterpart_match_order_require_pre_order: s.counterpart_match_order_require_pre_order,
    counterpart_match_order_require_time_ranges: s.counterpart_match_order_require_time_ranges,
    counterpart_match_order_require_min_odds: s.counterpart_match_order_require_min_odds,
    counterpart_match_order_require_max_odds: s.counterpart_match_order_require_max_odds,

    favorite_hold_enabled: s.favorite_hold_enabled,
    favorite_hold_flip_threshold: Math.min(1, Math.max(0, s.favorite_hold_flip_threshold)),
    favorite_hold_skip_seconds: Math.max(0, Math.floor(s.favorite_hold_skip_seconds)),
    favorite_hold_tier1_enabled: s.favorite_hold_tier1_enabled,
    favorite_hold_tier1_min_odds: Math.min(1, Math.max(0, s.favorite_hold_tier1_min_odds)),
    favorite_hold_tier1_dwell_seconds: Math.max(0, Math.floor(s.favorite_hold_tier1_dwell_seconds)),
    favorite_hold_tier2_enabled: s.favorite_hold_tier2_enabled,
    favorite_hold_tier2_min_rise: Math.min(1, Math.max(0, s.favorite_hold_tier2_min_rise)),
    favorite_hold_tier2_check_seconds: Math.max(0, Math.floor(s.favorite_hold_tier2_check_seconds)),
    favorite_hold_tier2_require_candle: s.favorite_hold_tier2_require_candle,
    favorite_hold_tier2_require_open_price: s.favorite_hold_tier2_require_open_price,
    favorite_hold_tier2_require_prev_windows: s.favorite_hold_tier2_require_prev_windows,
    favorite_hold_tier2_require_kronos: s.favorite_hold_tier2_require_kronos,
    favorite_hold_tier2_require_momentum: s.favorite_hold_tier2_require_momentum,
    favorite_hold_tier2_require_pre_order: s.favorite_hold_tier2_require_pre_order,
    favorite_hold_tier2_require_time_ranges: s.favorite_hold_tier2_require_time_ranges,
    favorite_hold_tier2_require_min_odds: s.favorite_hold_tier2_require_min_odds,
    favorite_hold_tier3_enabled: s.favorite_hold_tier3_enabled,
    favorite_hold_tier3_bypass_candle: s.favorite_hold_tier3_bypass_candle,
    favorite_hold_tier3_bypass_open_price: s.favorite_hold_tier3_bypass_open_price,
    favorite_hold_tier3_bypass_prev_windows: s.favorite_hold_tier3_bypass_prev_windows,
    favorite_hold_tier3_bypass_kronos: s.favorite_hold_tier3_bypass_kronos,
    favorite_hold_tier3_bypass_momentum: s.favorite_hold_tier3_bypass_momentum,
    favorite_hold_tier3_bypass_pre_order: s.favorite_hold_tier3_bypass_pre_order,
    favorite_hold_tier3_bypass_time_ranges: s.favorite_hold_tier3_bypass_time_ranges,
    favorite_hold_tier3_bypass_min_odds: s.favorite_hold_tier3_bypass_min_odds,

    kronos_check_enabled: s.kronos_check_enabled,
    kronos_require_direction: s.kronos_require_direction,
    kronos_min_confidence: Math.min(1, Math.max(0, s.kronos_min_confidence)),
    kronos_model: s.kronos_model,
    kronos_lookback: Math.max(1, Math.floor(s.kronos_lookback)),
    kronos_candle_timeframe_seconds: Math.max(1, Math.floor(s.kronos_candle_timeframe_seconds)),
    kronos_sample_count: Math.max(1, Math.floor(s.kronos_sample_count)),
    kronos_temperature: Math.max(0.01, s.kronos_temperature),
    kronos_top_p: Math.min(1, Math.max(0.01, s.kronos_top_p)),
    kronos_settled_precompute_enabled: s.kronos_settled_precompute_enabled,
    kronos_precompute_offset_seconds: Math.max(0, Math.floor(s.kronos_precompute_offset_seconds)),
    kronos_dynamic_warm_enabled: s.kronos_dynamic_warm_enabled,
    kronos_dynamic_warm_buffer_seconds: Math.max(0, Math.floor(s.kronos_dynamic_warm_buffer_seconds)),
  };
}

export function clientValidate(s: FormState, marketWindowSeconds: number, marketBucketSeconds: number): string[] {
  const errs: string[] = [];
  const cfg = toConfig(s);
  for (const f of REQUIRED_FIELDS) {
    const v = (cfg as Record<string, unknown>)[f];
    if (v === undefined || v === null || v === "") errs.push(`${f} is required`);
  }
  const tradeSize = Number(s.trade_size_usdc);
  if (!Number.isFinite(tradeSize) || tradeSize <= 0) errs.push("trade_size_usdc must be a positive number");
  if (Number.isNaN(Number(s.min_odds))) errs.push("min_odds must be numeric");
  if (Number.isNaN(Number(s.max_odds))) errs.push("max_odds must be numeric");
  if (Number(s.min_odds) >= Number(s.max_odds)) errs.push("min_odds must be < max_odds");
  // V3 multi-market: candle bounds are window-dependent.
  // ``total_candles = window_seconds / effective_timeframe``. We use
  // the effective value (configured override or market.bucket_seconds)
  // so disabling the override still validates correctly.
  if (s.candle_filter_enabled) {
    const tf = effectiveTimeframeSeconds(s.candle_timeframe_seconds, marketBucketSeconds);
    const totalCandles = Math.max(1, Math.floor(marketWindowSeconds / Math.max(1, tf)));
    const skip = Math.max(0, Math.floor(s.skip_first_candles));
    const remaining = Math.max(0, totalCandles - skip);
    // ``candles_to_check`` upper bound: cannot exceed the candles
    // remaining after skip; ``allow_live_candle`` doesn't extend the
    // pool size (the live candle replaces one closed candle).
    if (s.candles_to_check < 1 || s.candles_to_check > Math.max(1, remaining)) {
      errs.push(`candles_to_check must be 1..${Math.max(1, remaining)}`);
    }
    if (s.min_matching_candles < 0 || s.min_matching_candles > s.candles_to_check) {
      errs.push(`min_matching_candles must be 0..${s.candles_to_check}`);
    }
    if (s.allow_live_candle) {
      // Live candle index threshold: 0 = "any candle"; positive values
      // must point at a candle that survives the skip filter.
      if (!Number.isInteger(s.live_candle_minute) || s.live_candle_minute < 0 || s.live_candle_minute > totalCandles) {
        errs.push(`live_candle_minute must be 0 (any) or 1..${totalCandles}`);
      } else if (s.live_candle_minute > 0 && s.live_candle_minute <= skip) {
        errs.push(`live_candle_minute (${s.live_candle_minute}) is skipped by skip_first_candles (${skip})`);
      }
    }
    if (skip < 0 || skip >= totalCandles) {
      errs.push(`skip_first_candles must be 0..${totalCandles - 1}`);
    }
  }
  // Engine constraint: timeframe must divide the window. The UI's
  // allow-list already enforces this, but defend against operator
  // overrides via the JSON-edit path. Runs regardless of
  // candle_filter_enabled so a stale/overridden timeframe is still
  // flagged when the filter itself is toggled off.
  if (s.candle_timeframe_seconds > 0 && marketWindowSeconds % s.candle_timeframe_seconds !== 0) {
    errs.push(`candle_timeframe_seconds (${s.candle_timeframe_seconds}) must divide window_seconds (${marketWindowSeconds})`);
  }
  // V3 multi-market: ``decision_time`` must fit inside the selected
  // market's window. The hardcoded 299s ceiling here was a btc-5m-era
  // assumption that silently blocked saving the first config snapshot
  // for any longer-window market (15m/1h/4h).
  const decisionTimeMax = Math.max(0, Math.floor(marketWindowSeconds) - 1);
  if (parseDecisionTime(s.decision_time) > decisionTimeMax) {
    errs.push(`decision_time must be <= ${formatDecisionTime(decisionTimeMax)}`);
  }
  if (s.prev_windows_check_enabled) {
    if (!Number.isFinite(s.prev_windows_count) || s.prev_windows_count < 1) {
      errs.push("prev_windows_count must be >= 1");
    }
    if (
      !Number.isFinite(s.prev_windows_min_matches)
      || s.prev_windows_min_matches < 0
      || s.prev_windows_min_matches > s.prev_windows_count
    ) {
      errs.push(`prev_windows_min_matches must be 0..${s.prev_windows_count}`);
    }
  }
  return errs;
}

function FieldRow({ children }: { children: ReactNode }) {
  return <div style={{ width: "100%" }}>{children}</div>;
}

export function ConfigPage() {
  const qc = useQueryClient();
  // V3 multi-market: read the operator's currently focused market so
  // the candle-timeframe dropdown filters to that market's allow-list,
  // the live-candle / skip-first validators use its window size, AND
  // the active-config + activation calls scope to it (instead of the
  // legacy single-row pointer which only governs btc-5m).
  // Falls back to btc-5m's 300/60 when the catalogue is still loading.
  const marketCtx = useMarket();
  const marketId = marketCtx.marketId;
  const marketWindowSeconds = marketCtx.market?.window_seconds ?? 300;
  const marketBucketSeconds = marketCtx.market?.bucket_seconds ?? 60;
  const marketLabel = marketCtx.market?.display_name ?? marketCtx.market?.short_name ?? marketId;
  const activeQ = useQuery({
    queryKey: ["markets", marketId, "config-active"],
    queryFn: () => api.markets.activeConfig(marketId),
    staleTime: 0,
    enabled: Boolean(marketId),
  });
  const [form, setForm] = useState<FormState | null>(null);
  const [openFeaturePanels, setOpenFeaturePanels] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [savedVersionId, setSavedVersionId] = useState<string | null>(null);

  // V3: reset the form whenever the operator switches markets so the
  // ``useEffect`` below repopulates from the new market's active
  // config (or sensible defaults when none exists yet).
  useEffect(() => {
    setForm(null);
    setSavedVersionId(null);
    setNotes("");
  }, [marketId]);

  useEffect(() => {
    if (activeQ.data && form === null) {
      // Markets without an active per-market config still need a
      // populated form so the operator can author the first snapshot.
      // ``fromConfig({})`` yields the codebase defaults via its ``??``
      // fallbacks, mirroring trading-bot's ``DEFAULT_CONFIG_DATA``.
      const source = activeQ.data.active?.config ?? {};
      const nextForm = fromConfig(source);
      setForm(nextForm);
      setOpenFeaturePanels(FEATURE_TOGGLES.filter((feature) => nextForm[feature.key]).map((feature) => feature.key));
    }
  }, [activeQ.data, form]);

  const errs = useMemo(
    () => (form ? clientValidate(form, marketWindowSeconds, marketBucketSeconds) : []),
    [form, marketWindowSeconds, marketBucketSeconds],
  );

  const saveVersion = useMutation({
    mutationFn: async (full: Record<string, unknown>) => {
      const created = await api.config.create(full, notes || null);
      // 1011_storage_tiering: show what the activation needs from history
      // (Kronos horizon, candle backfill) before the blocking activation.
      try {
        const plan = await api.storage.activationPreview(marketId, created.config_version_id);
        if (plan.kronos_enabled) {
          const missing = ((plan.backfill?.missing_months as string[] | undefined) ?? []);
          notifications.show({
            color: plan.within_ceiling && missing.length === 0 ? "blue" : "red",
            title: "Kronos history",
            message: !plan.within_ceiling
              ? `Needs ${plan.requirement_human} but the data horizon is lower — activation will be refused`
              : missing.length > 0
                ? `Needs ${plan.requirement_human} but ${missing.join(", ")} are archived — restore them on Settings → Storage first`
                : `Building ${plan.requirement_human} of ${plan.uses_candles ? "candles" : "tick context"} + cache before activation`,
          });
        }
      } catch {
        // Preview is advisory; activation below reports the real outcome.
      }
      // V3 multi-market: activate ONLY for the currently focused
      // market. The legacy single-row activation endpoint is
      // intentionally NOT called here — using it would have re-pointed
      // the btc-5m bot at this snapshot regardless of which market the
      // operator was editing.
      await api.markets.activateConfig(marketId, created.config_version_id);
      return created;
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["markets", marketId, "config-active"] });
      qc.invalidateQueries({ queryKey: ["config", "versions"] });
      // Also invalidate market list queries so any "data-only" badges
      // elsewhere in the UI refresh once this market is promoted.
      // The catalogue used by ``useMarket`` / ``MarketSwitcher`` is
      // keyed under ``["v3", "markets"]`` (see ``hooks/useMarket.tsx``),
      // so the legacy ``["markets"]`` invalidation alone would NOT
      // refetch the dropdown -- the newly-promoted market would still
      // appear under "Available (disabled)" until a hard reload.
      qc.invalidateQueries({ queryKey: ["markets"] });
      qc.invalidateQueries({ queryKey: ["v3", "markets"] });
      setSavedVersionId(created.config_version_id);
      notifications.show({
        color: "green",
        title: `Config saved and activated for ${marketLabel}`,
        message: `Active: ${created.config_version_id.slice(0, 18)}...`,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  });

  if (activeQ.isLoading || !form) return <LoadingState />;
  if (activeQ.isError) return <ErrorState error={activeQ.error as Error} onRetry={() => activeQ.refetch()} />;

  const activeCfg = activeQ.data?.active ?? null;
  const currentForm: FormState = form;

  function updateFeatureToggle(key: FeatureToggleKey, checked: boolean) {
    setForm((prev) => (prev ? ({ ...prev, [key]: checked } as FormState) : prev));
    setOpenFeaturePanels((prev) => {
      if (checked) return prev.includes(key) ? prev : [...prev, key];
      return prev.filter((value) => value !== key);
    });
  }

  function handleUploadConfig() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          throw new Error("JSON must be an object of config fields.");
        }
        const nextForm = fromConfig(parsed as Record<string, unknown>);
        setForm(nextForm);
        setOpenFeaturePanels(
          FEATURE_TOGGLES.filter((feature) => nextForm[feature.key]).map((feature) => feature.key),
        );
        setSavedVersionId(null);
        notifications.show({
          color: "green",
          title: "Config loaded",
          message: `Form populated from ${file.name}.`,
        });
      } catch (err) {
        notifications.show({
          color: "red",
          title: "Failed to load config",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    };
    input.click();
  }

  function renderFeatureSettings(key: FeatureToggleKey): ReactNode {
    switch (key) {
      case "candle_filter_enabled": {
        const liveOn = currentForm.allow_live_candle;
        // V3 multi-market: derive all candle-related bounds from the
        // active market's window + the operator's timeframe choice
        // (falling back to bucket_seconds when timeframe is 0).
        const tfOptions = candleTimeframeOptionsFor(marketWindowSeconds);
        const tfEffective = effectiveTimeframeSeconds(
          currentForm.candle_timeframe_seconds,
          marketBucketSeconds,
        );
        const totalCandles = Math.max(1, Math.floor(marketWindowSeconds / Math.max(1, tfEffective)));
        const skip = Math.max(0, Math.floor(currentForm.skip_first_candles));
        const remaining = Math.max(1, totalCandles - skip);
        const candlesUpper = remaining;
        const matchUpper = currentForm.candles_to_check;
        // Live-candle options: 0 (any) plus every eligible candle
        // index that survives the skip filter (skip+1..totalCandles).
        const liveCandleOptions: Array<{ value: string; label: string }> = [
          { value: "0", label: `Any candle (${remaining} eligible)` },
        ];
        for (let i = skip + 1; i <= totalCandles; i += 1) {
          liveCandleOptions.push({ value: String(i), label: `Candle ${i}` });
        }
        return (
          <Stack gap="sm">
            <FieldRow>
              <Select
                label="Candle Timeframe"
                description={`Width of one candle. Defaults to the market's bucket size (${timeframeLabel(marketBucketSeconds)}). Allowed values for ${marketCtx.market?.short_name ?? "this market"}: ${tfOptions.map((o) => o.label).join(", ")}.`}
                value={String(currentForm.candle_timeframe_seconds)}
                onChange={(v) =>
                  setForm((prev) => {
                    if (!prev) return prev;
                    const nextTf = Number(v ?? 0);
                    const nextEffective = effectiveTimeframeSeconds(nextTf, marketBucketSeconds);
                    const nextTotal = Math.max(1, Math.floor(marketWindowSeconds / Math.max(1, nextEffective)));
                    // Re-clamp dependent fields so changing the
                    // timeframe never leaves the form in a state the
                    // validator rejects.
                    const nextSkip = Math.min(prev.skip_first_candles, Math.max(0, nextTotal - 1));
                    const nextRemaining = Math.max(1, nextTotal - nextSkip);
                    const nextCheck = Math.min(Math.max(1, prev.candles_to_check), nextRemaining);
                    const nextMin = Math.min(prev.min_matching_candles, nextCheck);
                    const nextLive = prev.live_candle_minute > nextTotal || prev.live_candle_minute <= nextSkip && prev.live_candle_minute > 0
                      ? 0
                      : prev.live_candle_minute;
                    return {
                      ...prev,
                      candle_timeframe_seconds: nextTf,
                      skip_first_candles: nextSkip,
                      candles_to_check: nextCheck,
                      min_matching_candles: nextMin,
                      live_candle_minute: nextLive,
                    };
                  })
                }
                data={[
                  { value: "0", label: `Default (${timeframeLabel(marketBucketSeconds)})` },
                  ...tfOptions.map((o) => ({ value: String(o.seconds), label: o.label })),
                ]}
                allowDeselect={false}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Skip First Candles"
                description={`Drop the lowest-index N qualifying candles before evaluation. Window has ${totalCandles} total ${timeframeLabel(tfEffective)} candles. Valid: whole number 0 – ${Math.max(0, totalCandles - 1)}. Recommended: 0 on 5m/15m, 1 – 2 on 1h/4h.`}
                min={0}
                max={Math.max(0, totalCandles - 1)}
                value={currentForm.skip_first_candles}
                onChange={(v) =>
                  setForm((prev) => {
                    if (!prev) return prev;
                    const nextSkip = Math.max(0, Math.min(totalCandles - 1, Math.floor(Number(v) || 0)));
                    const nextRemaining = Math.max(1, totalCandles - nextSkip);
                    const nextCheck = Math.min(Math.max(1, prev.candles_to_check), nextRemaining);
                    const nextMin = Math.min(prev.min_matching_candles, nextCheck);
                    // Drop the live-candle threshold if it now points
                    // at a candle the skip filter would discard.
                    const nextLive = prev.live_candle_minute > 0 && prev.live_candle_minute <= nextSkip
                      ? 0
                      : prev.live_candle_minute;
                    return {
                      ...prev,
                      skip_first_candles: nextSkip,
                      candles_to_check: nextCheck,
                      min_matching_candles: nextMin,
                      live_candle_minute: nextLive,
                    };
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Minimum Matching Candles"
                description="How many candles must match direction to allow entry. 0 = no candle requirement. Valid: whole number 0 – Candles to Check. Recommended: 2 – 3 out of 4."
                min={0}
                max={matchUpper}
                value={currentForm.min_matching_candles}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, min_matching_candles: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Candles to Check"
                description={
                  liveOn
                    ? `Number of recent ${timeframeLabel(tfEffective)} candles to evaluate (the in-progress live candle counts as one). Valid: whole number 1 – ${candlesUpper}. Recommended: 3 – 4 on 5m.`
                    : `Number of recent closed ${timeframeLabel(tfEffective)} candles to evaluate for agreement. Valid: whole number 1 – ${candlesUpper}. Recommended: 3 – 4 on 5m.`
                }
                min={1}
                max={candlesUpper}
                value={currentForm.candles_to_check}
                onChange={(v) =>
                  setForm((prev) => {
                    if (!prev) return prev;
                    const nextCheck = Math.max(1, Math.min(candlesUpper, Math.floor(Number(v) || 1)));
                    return {
                      ...prev,
                      candles_to_check: nextCheck,
                      min_matching_candles: Math.min(prev.min_matching_candles, nextCheck),
                    };
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Allow Live Candle"
                description="Count the in-progress (current) candle toward candle agreement using its running open->latest delta. Without this, only fully-closed candles count."
                checked={currentForm.allow_live_candle}
                onChange={(e) =>
                  setForm((prev) =>
                    prev ? { ...prev, allow_live_candle: e.currentTarget.checked } : prev,
                  )
                }
              />
            </FieldRow>
            {liveOn ? (
              <FieldRow>
                <Select
                  label="Live Candle Index"
                  description={`Earliest candle index on which the live-candle bonus may fire. E.g. Candle 4 → eligible from candle 4 onward. Defaults to 'Any candle' which enables on every eligible candle (after skip).`}
                  value={String(currentForm.live_candle_minute ?? 0)}
                  onChange={(v) =>
                    setForm((prev) =>
                      prev ? { ...prev, live_candle_minute: Number(v ?? 0) } : prev,
                    )
                  }
                  data={liveCandleOptions}
                  allowDeselect={false}
                />
              </FieldRow>
            ) : null}
          </Stack>
        );
      }
      case "stop_loss_enabled":
        return (
          <Stack gap="sm">
            <FieldRow>
              <TextInput
                label="Stop-loss Probability Threshold"
                description="Trigger when your side falls at or below this implied probability. Valid: decimal 0.0 – 1.0 (should be below Minimum Odds). Recommended: 0.30 – 0.50 (default 0.40)."
                value={currentForm.stop_loss_threshold}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, stop_loss_threshold: e.currentTarget.value } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Trigger Within Seconds"
                description="Only allow stop-loss when this many seconds or fewer remain. 0 means anytime. Valid: whole seconds 0 – 300. Recommended: 0, or 30 – 60 on 5m."
                min={0}
                max={300}
                value={currentForm.stop_loss_trigger_within_seconds}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, stop_loss_trigger_within_seconds: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Use BTC Delta Confirmation"
                description="Also require BTC move confirmation before stop-loss trigger."
                checked={currentForm.stop_loss_btc_delta_enabled}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, stop_loss_btc_delta_enabled: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <TextInput
                label="BTC Delta Dollars"
                description="Dollar move threshold used by BTC delta confirmation mode. Valid: decimal greater than 0 (USD). Recommended: BTC 20 – 60, ETH 2 – 5."
                value={currentForm.stop_loss_btc_delta_dollars}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, stop_loss_btc_delta_dollars: e.currentTarget.value } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="BTC Poll Interval (seconds)"
                description="How frequently BTC delta is sampled while confirmation is active. Valid: whole seconds 0 – 60. Recommended: 1 – 5."
                min={0}
                max={60}
                value={currentForm.stop_loss_btc_poll_interval_seconds}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, stop_loss_btc_poll_interval_seconds: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Select
                label="BTC Delta Mode"
                description="Delta behavior mode for stop-loss logic."
                value={currentForm.stop_loss_btc_delta_mode}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, stop_loss_btc_delta_mode: v ?? "trigger" } : prev))}
                data={[{ value: "trigger", label: "Trigger" }]}
                allowDeselect={false}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="BTC Confirm Seconds"
                description="Required confirmation duration before firing BTC delta stop-loss. 0 = fire at once. Valid: whole seconds 0 – 120. Recommended: 2 – 5."
                min={0}
                max={120}
                value={currentForm.stop_loss_btc_confirm_seconds}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, stop_loss_btc_confirm_seconds: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Last Candle Instant"
                description="When enabled, allow immediate final-candle stop-loss behavior."
                checked={currentForm.stop_loss_last_candle_instant}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, stop_loss_last_candle_instant: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Last Candle Seconds"
                description="Final-candle time window used by stop-loss logic near close. Valid: whole seconds 0 – 60. Recommended: 30 – 60 (one candle on 5m)."
                min={0}
                max={60}
                value={currentForm.stop_loss_last_candle_seconds}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, stop_loss_last_candle_seconds: Number(v) } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "take_profit_enabled":
        return (
          <Stack gap="sm">
            <FieldRow>
              <TextInput
                label="Take-profit Threshold"
                description="Sell as soon as your side reaches or exceeds this implied probability. Valid: decimal 0.0 – 1.0 (should be above Maximum Odds). Recommended: 0.90 – 0.98 (default 0.90)."
                value={currentForm.take_profit_threshold}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, take_profit_threshold: e.currentTarget.value } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Select
                label="Take-profit Order Type"
                description="REACTIVE submits on TP trigger; GTC_LIMIT keeps a resting TP limit order at the threshold."
                value={currentForm.take_profit_order_type}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, take_profit_order_type: (v ?? "REACTIVE").toUpperCase() } : prev))}
                data={[
                  { value: "REACTIVE", label: "Reactive (submit when TP triggers)" },
                  { value: "GTC_LIMIT", label: "Resting GTC limit at TP threshold" },
                ]}
                allowDeselect={false}
              />
            </FieldRow>
          </Stack>
        );
      case "skip_after_wins_enabled":
        return (
          <Stack gap="sm">
            <FieldRow>
              <NumberInput
                label="Win-streak Threshold"
                description="Number of consecutive wins that triggers entry pause. Valid: whole number 1 – 50. Recommended: 3 – 5."
                min={1}
                max={50}
                value={currentForm.skip_after_wins_threshold}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, skip_after_wins_threshold: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Skipped Losses to Resume"
                description="How many skipped hypothetical losses are required before trading resumes. Valid: whole number 1 – 10. Recommended: 1 – 2."
                min={1}
                max={10}
                value={currentForm.skip_after_wins_losses_to_resume}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, skip_after_wins_losses_to_resume: Number(v) } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "cascading_sizes_enabled":
        return (
          <FieldRow>
            <TextInput
              label="Size Sequence (USDC)"
              description="Comma-separated sizes. Example: 10,7,5. Last value repeats when sequence ends. Valid: comma-separated decimals greater than 0 (USDC). Recommended: 3 – 5 sizes."
              value={currentForm.cascading_sizes}
              onChange={(e) => setForm((prev) => (prev ? { ...prev, cascading_sizes: e.currentTarget.value } : prev))}
            />
          </FieldRow>
        );
      case "compounding_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Compounding accumulates <b>within the current win streak</b>. Each win adds
              {" "}<b>win % × profit</b> to a delta on top of the base size, so order sizes grow as
              the streak continues. <b>Any loss (or stop-loss / unresolved) resets the delta to $0</b>,
              so the next order returns to the base size. Size is floored at $0.
              Example: base $50, 10% win %, $10 profits — order #2 is $51, order #3 is $52, …
              until a loss snaps it back to $50.
            </Text>
            <FieldRow>
              <NumberInput
                label="Win Profit %"
                description="Percent of each win's profit added to the running size for the next order in the streak. Valid: whole percent 0 – 500. Recommended: 25 – 100."
                min={0}
                max={500}
                step={1}
                value={currentForm.compounding_win_pct}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, compounding_win_pct: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Max Size (USDC)"
                description="Cap the compounded order size at this amount. Set to 0 to disable the cap. Valid: decimal 0.0 or more (USDC). Recommended: 3 – 5× your base trade size."
                min={0}
                step={1}
                value={currentForm.compounding_max_size_usdc}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, compounding_max_size_usdc: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Stop Compounding After X Wins"
                description="Once this many consecutive wins are reached, compounding pauses. Set to 0 to disable. Valid: whole number 0 or more. Recommended: 3 – 5."
                min={0}
                step={1}
                value={currentForm.compounding_stop_after_wins}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, compounding_stop_after_wins: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Keep compounded size after stop"
                description="When on, order size stays at the compounded level after the stop threshold is reached (until a loss). When off, order size reverts to the default trade size during the stopped phase."
                checked={currentForm.compounding_stop_size_stay}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, compounding_stop_size_stay: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Resume from saved size after loss"
                description="When on, after a loss in the stopped phase the next compound cycle starts from the previously saved compounded size. When off, the next cycle starts fresh from $0 delta."
                checked={currentForm.compounding_resume_from_saved}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, compounding_resume_from_saved: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "late_buy_enabled":
        return (
          <Stack gap="sm">
            <FieldRow>
              <NumberInput
                label="Late Buy Window (seconds left)"
                description="Late-buy logic applies when remaining seconds are at or below this value. Valid: whole seconds 0 – 60. Recommended: 15 – 30."
                min={0}
                max={60}
                value={currentForm.late_buy_within_seconds}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, late_buy_within_seconds: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <TextInput
                label="Late Buy Trade Size (USDC)"
                description="Override size for late buys only. Set 0 to use base trade size. Valid: decimal 0.0 or more (USDC). Recommended: about half the base trade size."
                value={currentForm.late_buy_trade_size_usdc}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, late_buy_trade_size_usdc: e.currentTarget.value } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "force_buy_enabled":
        return <Text size="sm" c="dimmed">No additional settings for this feature.</Text>;
      case "trade_time_ranges_enabled":
        return (
          <FieldRow>
            <TextInput
              label="Allowed Time Ranges"
              description="Format: HH:MM-HH:MM,... (display tz). End is exclusive, so 00:00-00:15,00:15-00:30 don't overlap. Use 24:00 for the final range's end."
              value={currentForm.trade_time_ranges}
              onChange={(e) => setForm((prev) => (prev ? { ...prev, trade_time_ranges: e.currentTarget.value } : prev))}
            />
          </FieldRow>
        );
      case "trade_days_enabled": {
        const selected = new Set(parseTradeDays(currentForm.trade_days_allowed));
        const toggleDay = (d: string) => {
          const next = new Set(selected);
          if (next.has(d)) next.delete(d);
          else next.add(d);
          const serialized = DAY_TOKENS.filter((t) => next.has(t)).join(",");
          setForm((prev) => (prev ? { ...prev, trade_days_allowed: serialized } : prev));
        };
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Trading is only permitted on the selected weekdays. Weekday is evaluated in the
              display timezone. An empty selection with the toggle enabled means "never trade".
            </Text>
            <Group gap="md">
              {DAY_TOKENS.map((d) => (
                <Checkbox
                  key={d}
                  label={d}
                  checked={selected.has(d)}
                  onChange={() => toggleDay(d)}
                />
              ))}
            </Group>
            <FieldRow>
              <TextInput
                label="Raw (comma-separated)"
                description="Advanced: edit the raw list directly. Accepted tokens: MON,TUE,WED,THU,FRI,SAT,SUN."
                value={currentForm.trade_days_allowed}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, trade_days_allowed: e.currentTarget.value } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      }
      case "momentum_sell_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              <strong>How it works.</strong> After your buy fills, we track a rolling window of
              post-fill same-side ASK prices (rounded up to the nearest cent) and compute their
              average. The <em>SL sub-mode</em> fires when that average stays below your fill ask
              by a configured threshold. The <em>TP sub-mode</em> first waits for the average to
              peak above a profit threshold, then fires when it falls back below that level.
            </Text>

            <Text fw={600} size="sm" mt="xs">Window Configuration</Text>
            <FieldRow>
              <Select
                label="Window Type"
                description="Fixed seconds: always use this many seconds of history. Percentage: use this % of secs_left at fill time (e.g. 50% with 40s left = 20-second window)."
                value={currentForm.momentum_sell_window_type}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, momentum_sell_window_type: v ?? "number" } : prev))}
                data={[
                  { value: "number", label: "Fixed seconds" },
                  { value: "percentage", label: "% of remaining window at fill" },
                ]}
                allowDeselect={false}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label={currentForm.momentum_sell_window_type === "percentage" ? "Window Size (0–100%)" : "Window Size (seconds)"}
                description={currentForm.momentum_sell_window_type === "percentage"
                  ? "Percentage of secs_left at fill to average. E.g. 50 with 40s left → 20-second window. Valid: whole percent 1 – 100. Recommended: 25 – 50."
                  : "Number of per-second ask samples to include in the average. E.g. 10 → average last 10 seconds. Valid: whole seconds 1 – 300. Recommended: 10 – 20."}
                min={1}
                max={currentForm.momentum_sell_window_type === "percentage" ? 100 : 300}
                step={1}
                value={currentForm.momentum_sell_window_value}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, momentum_sell_window_value: Math.max(1, Number(v) || 1) } : prev))}
              />
            </FieldRow>

            <Text fw={600} size="sm" mt="xs">Stop-Loss Sub-mode</Text>
            <FieldRow>
              <Switch
                label="Enable stop-loss momentum sell"
                description="Fire a sell when the rolling average falls below your fill ask by the configured threshold for the required number of seconds."
                checked={currentForm.momentum_sell_sl_enabled}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, momentum_sell_sl_enabled: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Select
                label="SL Threshold Type"
                description="Odds points: avg must drop by this many points below fill ask. Percentage: avg must drop by this % of fill ask."
                value={currentForm.momentum_sell_sl_threshold_type}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, momentum_sell_sl_threshold_type: v ?? "points" } : prev))}
                data={[
                  { value: "points", label: "Odds points below fill" },
                  { value: "percentage", label: "% below fill ask" },
                ]}
                allowDeselect={false}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label={currentForm.momentum_sell_sl_threshold_type === "percentage" ? "SL Threshold (0–100%)" : "SL Threshold (odds points)"}
                description={currentForm.momentum_sell_sl_threshold_type === "percentage"
                  ? "Sell when avg < fill_ask × (1 − this%). E.g. fill 0.60, threshold 5 → sell when avg < 0.57. Valid: whole percent 0 – 100. Recommended: 3 – 10."
                  : "Sell when avg < fill_ask − this. E.g. fill 0.60, threshold 0.05 → sell when avg < 0.55. Valid: decimal 0.0 – 1.0 (odds points). Recommended: 0.03 – 0.10."}
                min={0}
                max={currentForm.momentum_sell_sl_threshold_type === "percentage" ? 100 : 1}
                step={currentForm.momentum_sell_sl_threshold_type === "percentage" ? 1 : 0.01}
                value={currentForm.momentum_sell_sl_threshold_value}
                onChange={(v) => {
                  const n = Number(v);
                  setForm((prev) => (prev ? { ...prev, momentum_sell_sl_threshold_value: Number.isFinite(n) ? Math.max(0, n) : 0 } : prev));
                }}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="SL Require Seconds (0 = instant)"
                description="How many consecutive seconds the average must stay below the SL threshold before selling. 0 = sell on the very first qualifying tick. Valid: whole seconds 0 – 120. Recommended: 2 – 5."
                min={0}
                max={120}
                step={1}
                value={currentForm.momentum_sell_sl_require_seconds}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, momentum_sell_sl_require_seconds: Math.max(0, Math.floor(Number(v) || 0)) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="SL Start at Seconds Left (0 = immediate)"
                description="Delay SL evaluation until this many seconds remain in the window. 0 = start as soon as the rolling buffer is full. E.g. 15 = SL checks only begin when ≤ 15s remain. Valid: whole seconds 0 – 300. Recommended: 0."
                min={0}
                max={300}
                step={1}
                value={currentForm.momentum_sell_sl_start_at_seconds_left}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, momentum_sell_sl_start_at_seconds_left: Math.max(0, Math.floor(Number(v) || 0)) } : prev))}
              />
            </FieldRow>

            <Text fw={600} size="sm" mt="xs">Take-Profit Sub-mode</Text>
            <FieldRow>
              <Switch
                label="Enable take-profit momentum sell"
                description="Once the rolling average reaches fill_ask + threshold, sell when it falls back below that level for the minimum required seconds."
                checked={currentForm.momentum_sell_tp_enabled}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, momentum_sell_tp_enabled: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Select
                label="TP Threshold Type"
                description="Odds points: average must rise by this many points above fill ask to activate TP. Percentage: average must rise by this % of fill ask."
                value={currentForm.momentum_sell_tp_threshold_type}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, momentum_sell_tp_threshold_type: v ?? "points" } : prev))}
                data={[
                  { value: "points", label: "Odds points above fill" },
                  { value: "percentage", label: "% above fill ask" },
                ]}
                allowDeselect={false}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label={currentForm.momentum_sell_tp_threshold_type === "percentage" ? "TP Threshold (0–100%)" : "TP Threshold (odds points)"}
                description={currentForm.momentum_sell_tp_threshold_type === "percentage"
                  ? "TP activates when avg ≥ fill_ask × (1 + this%). E.g. fill 0.60, threshold 5 → activates at avg ≥ 0.63. Valid: whole percent 0 – 100. Recommended: 3 – 10."
                  : "TP activates when avg ≥ fill_ask + this. E.g. fill 0.60, threshold 0.05 → activates at avg ≥ 0.65. Valid: decimal 0.0 – 1.0 (odds points). Recommended: 0.03 – 0.10."}
                min={0}
                max={currentForm.momentum_sell_tp_threshold_type === "percentage" ? 100 : 1}
                step={currentForm.momentum_sell_tp_threshold_type === "percentage" ? 1 : 0.01}
                value={currentForm.momentum_sell_tp_threshold_value}
                onChange={(v) => {
                  const n = Number(v);
                  setForm((prev) => (prev ? { ...prev, momentum_sell_tp_threshold_value: Number.isFinite(n) ? Math.max(0, n) : 0 } : prev));
                }}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="TP Min Seconds Below (0 = instant)"
                description="After TP activates, how many consecutive seconds the average must stay below the TP threshold before selling. 0 = sell on the first tick below the threshold. Valid: whole seconds 0 – 120. Recommended: 1 – 3."
                min={0}
                max={120}
                step={1}
                value={currentForm.momentum_sell_tp_min_seconds_below}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, momentum_sell_tp_min_seconds_below: Math.max(0, Math.floor(Number(v) || 0)) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="TP Start at Seconds Left (0 = immediate)"
                description="Delay TP evaluation until this many seconds remain in the window. 0 = start as soon as the rolling buffer is full. E.g. 15 = TP checks only begin when ≤ 15s remain. Valid: whole seconds 0 – 300. Recommended: 0."
                min={0}
                max={300}
                step={1}
                value={currentForm.momentum_sell_tp_start_at_seconds_left}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, momentum_sell_tp_start_at_seconds_left: Math.max(0, Math.floor(Number(v) || 0)) } : prev))}
              />
            </FieldRow>

            <Text fw={600} size="sm" mt="xs">General</Text>
            <FieldRow>
              <Switch
                label="Count profitable sells as wins"
                description="When on, a momentum sell that closes in profit counts as a win: it extends the win streak and continues compounding, just like a take-profit exit."
                checked={currentForm.momentum_sell_profit_is_win}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, momentum_sell_profit_is_win: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Loss-side sell breaks win streak"
                description="When on (default), a momentum sell that closes at a loss breaks the win streak and resets compounding state, same as a stop-loss. Turn off to treat loss-side momentum sells as neutral — neither incrementing nor resetting the streak / compounding sequence — so a defensive SL exit doesn't penalise the active cascade."
                checked={currentForm.momentum_sell_loss_breaks_streak}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, momentum_sell_loss_breaks_streak: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "pre_order_metrics_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              <strong>How it works.</strong> On every tick within the current window we
              incrementally track: (a) the average UP-side and DOWN-side mid odds, (b) the
              lowest UP mid and lowest DOWN mid observed so far, and (c) the lowest and
              highest BTC Δ% (from the window's open BTC price). When a placement decision
              fires, the position-side snapshot is captured, each enabled filter is
              evaluated, and the order is blocked if any filter's value falls outside its
              [min, max] band. The snapshot is persisted on the resulting order row so the
              Market Data page can replay the exact values the gate saw.
            </Text>

            <Text fw={600} size="sm" mt="xs">UP orders — avg UP mid observed</Text>
            <FieldRow>
              <Switch
                label="Enable UP avg-odds filter"
                description="For UP placements, block when the running average of UP mid odds is outside [min, max]."
                checked={currentForm.pre_order_up_avg_odds_filter_enabled}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, pre_order_up_avg_odds_filter_enabled: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Min avg UP odds"
                description="Valid: decimal 0.0 – 1.0 (at or below the max). Recommended: start wide (e.g. 0.40) and tighten from simulator results."
                min={0} max={1} step={0.01} decimalScale={4}
                value={currentForm.pre_order_up_avg_odds_min}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_up_avg_odds_min: Number(v) || 0 } : prev))}
              />
              <NumberInput
                label="Max avg UP odds"
                description="Valid: decimal 0.0 – 1.0 (at or above the min). Recommended: start wide (e.g. 0.95) and tighten from simulator results."
                min={0} max={1} step={0.01} decimalScale={4}
                value={currentForm.pre_order_up_avg_odds_max}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_up_avg_odds_max: Number(v) || 0 } : prev))}
              />
            </FieldRow>

            <Text fw={600} size="sm" mt="xs">DOWN orders — avg DOWN mid observed</Text>
            <FieldRow>
              <Switch
                label="Enable DOWN avg-odds filter"
                description="For DOWN placements, block when the running average of DOWN mid odds is outside [min, max]."
                checked={currentForm.pre_order_down_avg_odds_filter_enabled}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, pre_order_down_avg_odds_filter_enabled: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Min avg DOWN odds"
                description="Valid: decimal 0.0 – 1.0 (at or below the max). Recommended: start wide (e.g. 0.40) and tighten from simulator results."
                min={0} max={1} step={0.01} decimalScale={4}
                value={currentForm.pre_order_down_avg_odds_min}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_down_avg_odds_min: Number(v) || 0 } : prev))}
              />
              <NumberInput
                label="Max avg DOWN odds"
                description="Valid: decimal 0.0 – 1.0 (at or above the min). Recommended: start wide (e.g. 0.95) and tighten from simulator results."
                min={0} max={1} step={0.01} decimalScale={4}
                value={currentForm.pre_order_down_avg_odds_max}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_down_avg_odds_max: Number(v) || 0 } : prev))}
              />
            </FieldRow>

            <Text fw={600} size="sm" mt="xs">UP orders — lowest UP mid observed</Text>
            <FieldRow>
              <Switch
                label="Enable UP min-odds filter"
                description="For UP placements, block when the lowest UP mid reached in the window is outside [min, max]."
                checked={currentForm.pre_order_up_min_odds_filter_enabled}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, pre_order_up_min_odds_filter_enabled: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Min value"
                description="Valid: decimal 0.0 – 1.0 (at or below the max). Recommended: 0.30 – 0.50."
                min={0} max={1} step={0.01} decimalScale={4}
                value={currentForm.pre_order_up_min_odds_min}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_up_min_odds_min: Number(v) || 0 } : prev))}
              />
              <NumberInput
                label="Max value"
                description="Valid: decimal 0.0 – 1.0 (at or above the min). Recommended: 0.90 – 1.0."
                min={0} max={1} step={0.01} decimalScale={4}
                value={currentForm.pre_order_up_min_odds_max}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_up_min_odds_max: Number(v) || 0 } : prev))}
              />
            </FieldRow>

            <Text fw={600} size="sm" mt="xs">UP orders — lowest BTC Δ% from open</Text>
            <FieldRow>
              <Switch
                label="Enable UP min Δ% filter"
                description="For UP placements, block when the lowest BTC Δ% (most negative) observed in the window is outside [min, max] (percent, e.g. -0.05)."
                checked={currentForm.pre_order_up_min_pct_filter_enabled}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, pre_order_up_min_pct_filter_enabled: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Min Δ% (%)"
                description="Valid: decimal percent, negative allowed (e.g. -0.05 = 0.05% below the open; at or below the max). Recommended: -0.5 – 0."
                step={0.01} decimalScale={4}
                value={currentForm.pre_order_up_min_pct_min}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_up_min_pct_min: Number(v) || 0 } : prev))}
              />
              <NumberInput
                label="Max Δ% (%)"
                description="Valid: decimal percent, negative allowed (at or above the min). Recommended: 0 – 0.5."
                step={0.01} decimalScale={4}
                value={currentForm.pre_order_up_min_pct_max}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_up_min_pct_max: Number(v) || 0 } : prev))}
              />
            </FieldRow>

            <Text fw={600} size="sm" mt="xs">DOWN orders — lowest DOWN mid observed</Text>
            <FieldRow>
              <Switch
                label="Enable DOWN min-odds filter"
                description="For DOWN placements, block when the lowest DOWN mid reached in the window is outside [min, max]."
                checked={currentForm.pre_order_down_min_odds_filter_enabled}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, pre_order_down_min_odds_filter_enabled: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Min value"
                description="Valid: decimal 0.0 – 1.0 (at or below the max). Recommended: 0.30 – 0.50."
                min={0} max={1} step={0.01} decimalScale={4}
                value={currentForm.pre_order_down_min_odds_min}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_down_min_odds_min: Number(v) || 0 } : prev))}
              />
              <NumberInput
                label="Max value"
                description="Valid: decimal 0.0 – 1.0 (at or above the min). Recommended: 0.90 – 1.0."
                min={0} max={1} step={0.01} decimalScale={4}
                value={currentForm.pre_order_down_min_odds_max}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_down_min_odds_max: Number(v) || 0 } : prev))}
              />
            </FieldRow>

            <Text fw={600} size="sm" mt="xs">DOWN orders — highest BTC Δ% from open</Text>
            <FieldRow>
              <Switch
                label="Enable DOWN max Δ% filter"
                description="For DOWN placements, block when the highest BTC Δ% (most positive) observed in the window is outside [min, max] (percent)."
                checked={currentForm.pre_order_down_max_pct_filter_enabled}
                onChange={(e) => setForm((prev) => (prev ? { ...prev, pre_order_down_max_pct_filter_enabled: e.currentTarget.checked } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Min Δ% (%)"
                description="Valid: decimal percent, negative allowed (e.g. -0.05 = 0.05% below the open; at or below the max). Recommended: -0.5 – 0."
                step={0.01} decimalScale={4}
                value={currentForm.pre_order_down_max_pct_min}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_down_max_pct_min: Number(v) || 0 } : prev))}
              />
              <NumberInput
                label="Max Δ% (%)"
                description="Valid: decimal percent, negative allowed (at or above the min). Recommended: 0 – 0.5."
                step={0.01} decimalScale={4}
                value={currentForm.pre_order_down_max_pct_max}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_order_down_max_pct_max: Number(v) || 0 } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "odds_momentum_enabled":
        return (
          <Stack gap="sm">
            <FieldRow>
              <Switch
                label="Use Unique Odds Only"
                description="When enabled, momentum checks de-duplicate repeated odds values. Turn off to use every recorded tick (including duplicates)."
                checked={currentForm.odds_momentum_use_unique_odds}
                onChange={(e) => setForm((prev) => (prev
                  ? { ...prev, odds_momentum_use_unique_odds: e.currentTarget.checked }
                  : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Lookback Samples"
                description="Number of most-recent odds samples to evaluate for upward trend. Valid: whole number 1 – 290. Recommended: 5 – 10."
                min={1}
                max={290}
                value={currentForm.odds_momentum_lookback}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, odds_momentum_lookback: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Required Increasing Steps"
                description="Minimum count of rising sample-to-sample steps needed to pass filter. Valid: whole number 1 – 290 (at most Lookback Samples). Recommended: about half the lookback."
                min={1}
                max={290}
                value={currentForm.odds_momentum_required_increasing}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, odds_momentum_required_increasing: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Ignore Last Seconds"
                description="Bypass momentum filter in final seconds of window. Set 0 to never bypass. Valid: whole seconds 0 – 30. Recommended: 0 – 5."
                min={0}
                max={30}
                value={currentForm.odds_momentum_ignore_last_seconds}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, odds_momentum_ignore_last_seconds: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Avg Lookback Samples"
                description="Number of prior odds samples to average for spike detection. 0 = disabled. Valid: whole number 0 – 290. Recommended: 0, or 5 – 10."
                min={0}
                max={290}
                value={currentForm.odds_momentum_avg_lookback}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, odds_momentum_avg_lookback: Number(v) } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Max Avg Difference"
                description="Max allowed gap (current odds − avg of prior N). Fails if gap exceeds this. E.g. 0.35 = 35 pts. Valid: decimal 0.0 – 1.0 (odds points). Recommended: 0.10 – 0.30 (default 0.50)."
                min={0}
                max={1}
                step={0.01}
                decimalScale={4}
                value={currentForm.odds_momentum_avg_max_diff}
                onChange={(v) => setForm((prev) => (prev ? { ...prev, odds_momentum_avg_max_diff: Number(v) } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "open_price_gate_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Before entering, require the <b>current price</b> to be on the
              picked side of the window&apos;s <b>open price</b>: for an UP pick
              the current price must be strictly above open; for a DOWN pick it
              must be strictly below open. The open is the first price sample of
              the window and the current is the latest sample, both taken from
              the same market feed &mdash; so this works for any market
              (BTC, ETH, &hellip;).
            </Text>
            <Text size="xs" c="dimmed">
              <b>Cold start:</b> if no price sample has arrived yet for the
              window, entries are blocked (fail-closed). There are no extra
              settings &mdash; the direction is derived from the picked side.
            </Text>
          </Stack>
        );
      case "open_price_override_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              When every other check passes <b>except</b> the candle filter,
              still allow the entry as long as the current price is on the
              correct side of the window open (UP: above open; DOWN: below
              open). This overrides only the candle sub-check &mdash; the odds
              band, no-buy window, and any other enabled gates still apply.
            </Text>
            <Text size="xs" c="dimmed">
              Set <b>within seconds</b> to <Code style={monoStyle}>0</Code> to
              allow the override <b>right away</b> (any time after the decision
              point), or to a positive value to only allow it in the last X
              seconds of the window.
            </Text>
            <FieldRow>
              <NumberInput
                label="Within seconds (0 = right away)"
                description="Only override the candle check when secs_left <= this value. 0 means allow any time after decision time. Valid: whole seconds 0 or more. Recommended: 0, or 30 – 60 on 5m."
                min={0}
                value={currentForm.open_price_override_within_seconds}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  open_price_override_within_seconds: Math.max(0, Math.floor(Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "open_price_cushion_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              A stricter companion to the direction gate. Before entering,
              require the <b>current price</b> to be at least{" "}
              <b>min cushion</b> basis points <b>beyond</b> the window{" "}
              <b>open</b> in the picked direction (UP: above open; DOWN: below
              open). This rejects at-the-money / wrong-side entries where
              confident-looking favourite odds mask a coin-flip outcome.
            </Text>
            <Text size="xs" c="dimmed">
              The threshold is in <b>basis points of the open</b>, so it is
              market-agnostic (e.g. 2 bps &asymp; $15 on a $77k BTC window,
              &asymp; $0.48 on a $2.4k ETH window). <b>Cold start:</b> if no
              price sample has arrived yet, entries are blocked (fail-closed).
            </Text>
            <FieldRow>
              <NumberInput
                label="Min cushion (bps of open)"
                description="Current price must be at least this many basis points past the open in the bet direction. e.g. 2 = 0.02%. Valid: decimal 0.0 or more (0.5 steps). Recommended: 1 – 5 bps."
                min={0}
                step={0.5}
                decimalScale={2}
                value={currentForm.open_price_cushion_min_bps}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  open_price_cushion_min_bps: Math.max(0, Number(v) || 0),
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "prev_windows_check_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Before entering, look back at the last <b>N</b> resolved windows
              (candles) for this market and require at least <b>M</b> of them
              to have closed in the same direction as the bot's pick. On this{" "}
              {marketWindowSeconds / 60}-minute market, each &quot;previous
              candle&quot; is one full resolved market window.
            </Text>
            <Text size="xs" c="dimmed">
              <b>Cold start:</b> until the bot has observed <b>N</b> resolved
              windows for this market, entries are skipped (fail-closed).
              Neutral / unresolved windows never count as a match.
            </Text>
            <FieldRow>
              <NumberInput
                label="Windows to check (N)"
                description="How many of the most recent resolved windows to inspect. Valid: whole number 1 – 20. Recommended: 2 – 4 (default 3)."
                min={1}
                max={20}
                value={currentForm.prev_windows_count}
                onChange={(v) => setForm((prev) => {
                  if (!prev) return prev;
                  const nextCount = Math.max(1, Math.floor(Number(v) || 0));
                  const clampedMin = Math.min(prev.prev_windows_min_matches, nextCount);
                  return { ...prev, prev_windows_count: nextCount, prev_windows_min_matches: clampedMin };
                })}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Required matches (M)"
                description="Number of the last N windows that must agree with the picked direction. Valid: whole number 0 – Windows to Check. Recommended: one less than N (default 2 of 3)."
                min={0}
                max={currentForm.prev_windows_count}
                value={currentForm.prev_windows_min_matches}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  prev_windows_min_matches: Math.min(
                    Math.max(0, Math.floor(Number(v) || 0)),
                    Math.max(1, Math.floor(prev.prev_windows_count)),
                  ),
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "pre_decision_avg_odds_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Average each side&apos;s valid bid/ask midpoints from window open strictly before
              configured decision time. The averages freeze at that cutoff and never change
              during monitoring. The selected order side must meet the minimum. Early entries
              and missing odds are blocked; counterpart and Favorite Hold overrides cannot bypass this gate.
            </Text>
            <NumberInput
              label="Minimum pre-decision average odds"
              description="Inclusive threshold on the selected side, e.g. 0.53 = 53%. Independent of the rolling Pre-order Metrics filters. Valid: decimal 0.0 – 1.0. Recommended: 0.52 – 0.60 (default 0.53)."
              min={0} max={1} step={0.01} decimalScale={4}
              value={currentForm.pre_decision_avg_odds_min}
              onChange={(v) => setForm((prev) => (prev ? { ...prev, pre_decision_avg_odds_min: Number(v) || 0 } : prev))}
            />
          </Stack>
        );
      case "band_dwell_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Before entering, require the picked side&apos;s <b>mid odds</b> to
              have spent at least <b>N seconds</b> inside the configured{" "}
              <b>min/max odds band</b> during the window, counted from window
              start up to <b>decision time</b>. Favourites that only just
              spiked into the band are filtered out; ones that held the band
              are allowed through.
            </Text>
            <Text size="xs" c="dimmed">
              The dwell count is fixed per window+side once decision time is
              reached (it never grows afterwards), applies to <b>every</b>{" "}
              entry path (favourite, counterpart override, matching order),
              and has no counterpart bypass. Set seconds to 0 to disable
              while keeping the toggle on.
            </Text>
            <FieldRow>
              <NumberInput
                label="Min time in range (seconds)"
                description="Picked side's mid must have sat inside [min odds, max odds] for at least this many pre-decision seconds. Valid: whole seconds 0 or more (below decision time). Recommended: 10 – 30 on 5m."
                min={0}
                value={currentForm.band_dwell_min_seconds}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  band_dwell_min_seconds: Math.max(0, Math.floor(Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Require time in range > opposite side"
                description="Also require the picked side's pre-decision time in range to STRICTLY exceed the opposite side's (ties fail). Useful when both sides visited the range at different phases of the window."
                checked={currentForm.band_dwell_require_exceeds_opposite}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  band_dwell_require_exceeds_opposite: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "flip_gate_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Tracks, per side, how many times that side&apos;s <b>mid odds</b>{" "}
              dropped from at/above the <b>flip threshold</b> to below it,
              sampled once per second from the end of the <b>warm-up skip</b>{" "}
              onward. At decision time the picked side&apos;s count must be at
              most <b>max flips</b>. Ticks inside the warm-up are ignored
              entirely; the first tick after it only seeds the state.
            </Text>
            <Text size="xs" c="dimmed">
              Applies to the standard decision-time path only (Favorite Hold
              early tiers already require an unflipped favourite). Hard gate:
              no counterpart or Favorite Hold tier-3 bypass.
            </Text>
            <FieldRow>
              <NumberInput
                label="Max flips"
                description="Maximum number of drops below the flip threshold allowed on the picked side. 0 = the side must never have flipped since the warm-up skip. Valid: whole number 0 or more. Recommended: 0 – 1."
                min={0}
                value={currentForm.flip_gate_max_flips}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  flip_gate_max_flips: Math.max(0, Math.floor(Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Skip first (seconds)"
                description="Ignore the first N seconds of the window entirely. Valid: whole seconds 0 or more (below decision time). Recommended: 90 on 5m, 420 on 15m, 1800 on 1h, 7200 on 4h."
                min={0}
                value={currentForm.flip_gate_skip_seconds}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  flip_gate_skip_seconds: Math.max(0, Math.floor(Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Flip threshold"
                description="A side flips when its mid goes from >= this value to below it between consecutive seconds. Valid: decimal 0.0 – 1.0. Recommended: 0.50 (default)."
                min={0} max={1} step={0.01} decimalScale={4}
                value={currentForm.flip_gate_flip_threshold}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  flip_gate_flip_threshold: Math.min(1, Math.max(0, Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "peak_drawdown_gate_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Two sub-checks, each disabled at <b>0</b>. The drawdown check compares the
              picked side&apos;s current mid with the <b>highest mid it reached</b> earlier
              in the window; the retention check compares the underlying&apos;s current
              move from the window open with its <b>peak favourable move</b>. Fails closed
              without data; no counterpart / Favorite Hold bypass.
            </Text>
            <FieldRow>
              <NumberInput
                label="Max drop from peak odds"
                description="Block when peak mid - current mid exceeds this. E.g. 0.06 rejects buying at 0.85 after a 0.94 peak. 0 = skip this check. Valid: decimal 0.0 – 1.0 (odds points). Recommended: 0.04 – 0.08."
                min={0}
                max={1}
                step={0.01}
                decimalScale={3}
                value={currentForm.max_drop_from_peak_odds}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, max_drop_from_peak_odds: Math.max(0, Number(v) || 0),
                } : prev))}
              />
              <NumberInput
                label="Min price excursion retention"
                description="Require (current move from open) / (peak favourable move from open) >= this fraction. 0.55 = still holding 55% of the best excursion. 0 = skip this check. Valid: decimal 0.0 – 1.0. Recommended: 0.50 – 0.70."
                min={0}
                max={1}
                step={0.05}
                decimalScale={3}
                value={currentForm.min_price_excursion_retention}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, min_price_excursion_retention: Math.min(1, Math.max(0, Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "vol_cushion_gate_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              z = signed (current &minus; open) / (stdev of 1-second price changes observed so
              far in the window &times; &radic;secs_left). A fixed bps cushion is a near-lock on a
              quiet night and a coin-flip during a whipsaw; this demands a larger cushion when
              volatile or far from close. Fails closed until at least two price changes exist.
            </Text>
            <FieldRow>
              <NumberInput
                label="Min cushion z-score"
                description="Minimum z-score the price lead over the open must reach; higher = pickier. Valid: decimal 0.0 or more. Recommended: 1.5 – 2.5."
                min={0}
                step={0.1}
                decimalScale={3}
                value={currentForm.vol_cushion_min_z}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, vol_cushion_min_z: Math.max(0, Number(v) || 0),
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "underlying_velocity_gate_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Compares the current reference price with the newest tick at least K seconds
              old. Fails closed until such a tick exists.
            </Text>
            <FieldRow>
              <NumberInput
                label="Lookback (seconds)"
                description="How many seconds back to compare the reference price against. Valid: whole seconds 1 – 600. Recommended: 3 – 10."
                min={1}
                max={600}
                value={currentForm.underlying_velocity_lookback_seconds}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, underlying_velocity_lookback_seconds: Math.max(1, Math.trunc(Number(v) || 1)),
                } : prev))}
              />
              <NumberInput
                label="Min move (bps toward pick)"
                description="0 = must not be moving against the pick; negative tolerates a small adverse drift. Valid: decimal, negative allowed (0.5 steps). Recommended: 0 – 2 bps."
                step={0.5}
                decimalScale={3}
                value={currentForm.underlying_velocity_min_bps}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, underlying_velocity_min_bps: Number(v) || 0,
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "open_side_gate_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Streak counts consecutive 1 Hz samples strictly on the picked side of the window
              open ending at the current tick. Crossings count sign changes of (price &minus; open)
              since window start. Each sub-check is disabled at 0.
            </Text>
            <FieldRow>
              <NumberInput
                label="Open-side streak (seconds)"
                description="Consecutive seconds the reference price must have stayed on the picked side of the open. 0 = skip this check. Valid: whole seconds 0 or more. Recommended: 10 – 30."
                min={0}
                value={currentForm.open_side_streak_seconds}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, open_side_streak_seconds: Math.max(0, Math.trunc(Number(v) || 0)),
                } : prev))}
              />
              <NumberInput
                label="Max open crossings"
                description="0 disables; 1 allows at most one cross. Valid: whole number 0 or more. Recommended: 1 – 3."
                min={0}
                value={currentForm.max_open_crosses}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, max_open_crosses: Math.max(0, Math.trunc(Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "max_spread_gate_enabled":
        return (
          <Stack gap="sm">
            <FieldRow>
              <NumberInput
                label="Max bid-ask spread"
                description="Maximum (ask - bid) on the picked side at the entry tick, in odds (0.02 = 2 cents). Valid: decimal 0.0 – 1.0. Recommended: 0.02 – 0.04."
                min={0}
                max={1}
                step={0.01}
                decimalScale={3}
                value={currentForm.max_spread}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, max_spread: Math.max(0, Number(v) || 0),
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "counterpart_confirm_gate_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Unlike the Counterpart Override (which <b>adds</b> entries), this gate only
              <b> removes</b> them. Reads the paired BTC/ETH window of the same timeframe.
            </Text>
            <FieldRow>
              <Select
                label="Confirmation mode"
                value={currentForm.counterpart_confirm_mode}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, counterpart_confirm_mode: v === "favorite" ? "favorite" : "price",
                } : prev))}
                data={[
                  { value: "price", label: "Counterpart underlying move vs its open" },
                  { value: "favorite", label: "Counterpart favourite side must match" },
                ]}
                allowDeselect={false}
              />
              <NumberInput
                label="Max opposing counterpart move (bps)"
                description="'price' mode: largest counterpart move AGAINST the picked direction still tolerated. 0 = any opposing move blocks. Valid: decimal 0.0 or more (0.5 steps). Recommended: 0 – 3 bps."
                min={0}
                step={0.5}
                decimalScale={3}
                disabled={currentForm.counterpart_confirm_mode !== "price"}
                value={currentForm.counterpart_confirm_max_opposing_bps}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, counterpart_confirm_max_opposing_bps: Math.max(0, Number(v) || 0),
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "exit_require_underlying_breach":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Applies to the odds Stop Loss and the Momentum Sell SL / TP&rarr;SL branches in
              both live and simulation. Momentum Sell TP and regular Take Profit are unaffected.
            </Text>
            <FieldRow>
              <NumberInput
                label="Breach threshold (bps from open)"
                description="Exits allowed only when the signed move from the window open toward the position is <= this. 0 = at or past the open; positive values allow exits while a small cushion remains. Valid: decimal, negative allowed (0.5 steps). Recommended: 0 – 2 bps."
                step={0.5}
                decimalScale={3}
                value={currentForm.exit_underlying_breach_bps}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, exit_underlying_breach_bps: Number(v) || 0,
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      case "counterpart_override_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              {COUNTERPART_FIELD_DESCRIPTIONS.counterpart_override_enabled}
            </Text>
            <Text size="xs" c="dimmed">
              &quot;Decided&quot; = the counterpart favourite&apos;s mid odds are
              &ge; the threshold, and (optionally) have stayed there for the
              stability seconds. Wired on all markets; expect the strongest
              effect on 5m/15m.
            </Text>
            <FieldRow>
              <Switch
                label="Follow Counterpart Direction"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_follow_direction}
                checked={currentForm.counterpart_follow_direction}
                onChange={(e) => { const checked = e.currentTarget.checked; setForm((prev) => prev ? { ...prev, counterpart_follow_direction: checked } : prev); }}
              />
              <Switch
                label="Exclusive counterpart entries"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_follow_exclusive}
                disabled={!currentForm.counterpart_follow_direction}
                checked={currentForm.counterpart_follow_exclusive}
                onChange={(e) => { const checked = e.currentTarget.checked; setForm((prev) => prev ? { ...prev, counterpart_follow_exclusive: checked } : prev); }}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Counterpart odds threshold"
                description="Counterpart favourite mid must be >= this to count as decided. Valid: decimal 0.0 – 1.0. Recommended: 0.90 – 0.97 (default 0.95)."
                min={0}
                max={1}
                step={0.01}
                decimalScale={3}
                value={currentForm.counterpart_odds_threshold}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_odds_threshold: Math.min(1, Math.max(0, Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Decide within (% of window remaining, 0 = anytime)"
                description="Only fire when this market's fraction-of-window remaining <= this percent. 0 = any time after the decision point. Valid: whole percent 0 – 100. Recommended: 0, or 20 – 40."
                min={0}
                max={100}
                step={1}
                value={currentForm.counterpart_decide_within_pct}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_decide_within_pct: Math.min(100, Math.max(0, Number(v) || 0)),
                } : prev))}
              />
              {(() => {
                // Translate the % into the concrete elapsed-second window the
                // override can actually ENTER, accounting for decision time
                // and the no-buy cutoff (which is never bypassed).
                const W = marketWindowSeconds;
                const D = parseDecisionTime(currentForm.decision_time);
                const NB = Math.max(0, Number(currentForm.no_buy_window_seconds) || 0);
                const P = Math.max(0, Number(currentForm.counterpart_decide_within_pct) || 0);
                // Earliest secs-left the timing gate allows, bounded by decision time.
                const maxSecsLeft = P > 0 ? Math.min(W - D, Math.floor((P / 100) * W)) : (W - D);
                const startElapsed = W - maxSecsLeft;
                // Latest elapsed second before the no-buy cutoff blocks entry.
                const endElapsed = NB > 0 ? W - NB - 1 : W - 1;
                if (maxSecsLeft < 0) {
                  return <Text size="xs" c="orange" mt={4}>Decision time ({D}s) is at/after window close ({W}s) — the override never fires.</Text>;
                }
                if (startElapsed > endElapsed) {
                  return <Text size="xs" c="orange" mt={4}>At {P}% the earliest entry is {startElapsed}s, but the {NB}s no-buy cutoff blocks everything after {endElapsed}s — the override can never place. Raise the % or lower the no-buy window.</Text>;
                }
                return <Text size="xs" c="dimmed" mt={4}>Effective entry window: <b>{startElapsed}s → {endElapsed}s</b> into the {W}s window (decision time {D}s, no-buy {NB}s).</Text>;
              })()}
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Stability seconds (0 = no requirement)"
                description="Counterpart favourite must have stayed >= threshold for at least this many consecutive seconds. Valid: whole seconds 0 or more. Recommended: 3 – 10."
                min={0}
                value={currentForm.counterpart_stable_seconds}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_stable_seconds: Math.max(0, Math.floor(Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Require this market to be trending in that direction"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_require_trend}
                checked={currentForm.counterpart_require_trend}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_require_trend: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            <Text size="xs" c="dimmed">
              <b>Gates this override may bypass</b> (select any):
            </Text>
            <FieldRow>
              <Switch
                label="Bypass candle / momentum-agreement filter"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_bypass_candle}
                checked={currentForm.counterpart_bypass_candle}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_bypass_candle: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Bypass open-price direction gate"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_bypass_open_price}
                checked={currentForm.counterpart_bypass_open_price}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_bypass_open_price: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Bypass previous-windows direction gate"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_bypass_prev_windows}
                checked={currentForm.counterpart_bypass_prev_windows}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_bypass_prev_windows: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Bypass Kronos forecast gate"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_bypass_kronos}
                checked={currentForm.counterpart_bypass_kronos}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_bypass_kronos: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Bypass odds-momentum gate"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_bypass_momentum}
                checked={currentForm.counterpart_bypass_momentum}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_bypass_momentum: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Bypass pre-order metrics filter"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_bypass_pre_order}
                checked={currentForm.counterpart_bypass_pre_order}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_bypass_pre_order: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Bypass trade time ranges gate"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_bypass_time_ranges}
                checked={currentForm.counterpart_bypass_time_ranges}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_bypass_time_ranges: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Bypass min odds bound"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_bypass_min_odds}
                checked={currentForm.counterpart_bypass_min_odds}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_bypass_min_odds: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <Switch
                label="Bypass max odds bound"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_bypass_max_odds}
                checked={currentForm.counterpart_bypass_max_odds}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_bypass_max_odds: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>

            <FieldRow>
              <Switch
                label="Place matching order"
                description={COUNTERPART_FIELD_DESCRIPTIONS.counterpart_match_order_enabled}
                checked={currentForm.counterpart_match_order_enabled}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  counterpart_match_order_enabled: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            {currentForm.counterpart_match_order_enabled && (
              <>
                <Text size="xs" c="dimmed">
                  <b>Counterpart matching-order — require</b> (all off = place
                  immediately in the counterpart&apos;s direction, bypassing
                  every check):
                </Text>
                {([
                  ["counterpart_match_order_require_trend", "Require trend"],
                  ["counterpart_match_order_require_candle", "Require candle agreement"],
                  ["counterpart_match_order_require_open_price", "Require open-price direction"],
                  ["counterpart_match_order_require_prev_windows", "Require previous-windows direction"],
                  ["counterpart_match_order_require_kronos", "Require Kronos forecast"],
                  ["counterpart_match_order_require_momentum", "Require odds-momentum"],
                  ["counterpart_match_order_require_pre_order", "Require pre-order metrics"],
                  ["counterpart_match_order_require_time_ranges", "Require trade-time ranges"],
                  ["counterpart_match_order_require_min_odds", "Require min odds"],
                  ["counterpart_match_order_require_max_odds", "Require max odds"],
                ] as const).map(([key, label]) => (
                  <FieldRow key={key}>
                    <Switch
                      label={label}
                      description={COUNTERPART_FIELD_DESCRIPTIONS[key]}
                      checked={currentForm[key]}
                      onChange={(e) => setForm((prev) => (prev ? {
                        ...prev,
                        [key]: e.currentTarget.checked,
                      } : prev))}
                    />
                  </FieldRow>
                ))}
              </>
            )}
          </Stack>
        );
      case "favorite_hold_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              {FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_enabled}
            </Text>
            <Text size="xs" c="dimmed">
              Favorite Hold has no max-odds bypass of its own. Other enabled
              entry paths keep their own rules. The no-buy-window cutoff and
              trade-days gate still apply, and at most one order is placed per window.
            </Text>
            <FieldRow>
              <NumberInput
                label={FAVORITE_HOLD_FIELD_LABELS.favorite_hold_flip_threshold}
                description={FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_flip_threshold}
                min={0}
                max={1}
                step={0.01}
                decimalScale={3}
                value={currentForm.favorite_hold_flip_threshold}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  favorite_hold_flip_threshold: Math.min(1, Math.max(0, Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label={FAVORITE_HOLD_FIELD_LABELS.favorite_hold_skip_seconds}
                description={FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_skip_seconds}
                min={0}
                value={currentForm.favorite_hold_skip_seconds}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev,
                  favorite_hold_skip_seconds: Math.max(0, Number(v) || 0),
                } : prev))}
              />
            </FieldRow>
            <Divider label="Tier 1 — dwell instant buy" />
            <FieldRow>
              <Switch
                label={FAVORITE_HOLD_FIELD_LABELS.favorite_hold_tier1_enabled}
                description={FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_tier1_enabled}
                checked={currentForm.favorite_hold_tier1_enabled}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  favorite_hold_tier1_enabled: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            {currentForm.favorite_hold_tier1_enabled && (
              <>
                <FieldRow>
                  <NumberInput
                    label={FAVORITE_HOLD_FIELD_LABELS.favorite_hold_tier1_min_odds}
                    description={FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_tier1_min_odds}
                    min={0}
                    max={1}
                    step={0.01}
                    decimalScale={3}
                    value={currentForm.favorite_hold_tier1_min_odds}
                    onChange={(v) => setForm((prev) => (prev ? {
                      ...prev,
                      favorite_hold_tier1_min_odds: Math.min(1, Math.max(0, Number(v) || 0)),
                    } : prev))}
                  />
                </FieldRow>
                <FieldRow>
                  <NumberInput
                    label={FAVORITE_HOLD_FIELD_LABELS.favorite_hold_tier1_dwell_seconds}
                    description={FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_tier1_dwell_seconds}
                    min={0}
                    value={currentForm.favorite_hold_tier1_dwell_seconds}
                    onChange={(v) => setForm((prev) => (prev ? {
                      ...prev,
                      favorite_hold_tier1_dwell_seconds: Math.max(0, Math.floor(Number(v) || 0)),
                    } : prev))}
                  />
                </FieldRow>
              </>
            )}
            <Divider label="Tier 2 — early gated entry" />
            <FieldRow>
              <Switch
                label="Enable tier 2"
                description={FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_tier2_enabled}
                checked={currentForm.favorite_hold_tier2_enabled}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  favorite_hold_tier2_enabled: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            {currentForm.favorite_hold_tier2_enabled && (
              <>
                <FieldRow>
                  <NumberInput
                    label="Tier 2 min average rise"
                    description={FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_tier2_min_rise}
                    min={0}
                    max={1}
                    step={0.01}
                    decimalScale={3}
                    value={currentForm.favorite_hold_tier2_min_rise}
                    onChange={(v) => setForm((prev) => (prev ? {
                      ...prev,
                      favorite_hold_tier2_min_rise: Math.min(1, Math.max(0, Number(v) || 0)),
                    } : prev))}
                  />
                </FieldRow>
                <FieldRow>
                  <NumberInput
                    label="Tier 2 check seconds (elapsed into window)"
                    description={FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_tier2_check_seconds}
                    min={0}
                    value={currentForm.favorite_hold_tier2_check_seconds}
                    onChange={(v) => setForm((prev) => (prev ? {
                      ...prev,
                      favorite_hold_tier2_check_seconds: Math.max(0, Math.floor(Number(v) || 0)),
                    } : prev))}
                  />
                </FieldRow>
                <Text size="xs" c="dimmed">
                  <b>Tier 2 — gates to re-impose</b> (all off = only the rise
                  test + the always-on invariants; each toggle re-imposes one
                  gate):
                </Text>
                {([
                  ["favorite_hold_tier2_require_candle", "Require candle agreement"],
                  ["favorite_hold_tier2_require_open_price", "Require open-price direction (+ cushion)"],
                  ["favorite_hold_tier2_require_prev_windows", "Require previous-windows direction"],
                  ["favorite_hold_tier2_require_kronos", "Require Kronos forecast"],
                  ["favorite_hold_tier2_require_momentum", "Require odds-momentum"],
                  ["favorite_hold_tier2_require_pre_order", "Require pre-order metrics"],
                  ["favorite_hold_tier2_require_time_ranges", "Require trade-time ranges"],
                  ["favorite_hold_tier2_require_min_odds", "Require min odds"],
                ] as const).map(([key, label]) => (
                  <FieldRow key={key}>
                    <Switch
                      label={label}
                      description={FAVORITE_HOLD_FIELD_DESCRIPTIONS[key]}
                      checked={currentForm[key]}
                      onChange={(e) => setForm((prev) => (prev ? {
                        ...prev,
                        [key]: e.currentTarget.checked,
                      } : prev))}
                    />
                  </FieldRow>
                ))}
              </>
            )}
            <Divider label="Tier 3 — decision-time gate bypass" />
            <FieldRow>
              <Switch
                label="Enable tier 3"
                description={FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_tier3_enabled}
                checked={currentForm.favorite_hold_tier3_enabled}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev,
                  favorite_hold_tier3_enabled: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            {currentForm.favorite_hold_tier3_enabled && (
              <>
                <Text size="xs" c="dimmed">
                  <b>Tier 3 — gates to bypass at decision time</b> while the
                  opening favourite has never flipped (max odds can never be
                  bypassed):
                </Text>
                {([
                  ["favorite_hold_tier3_bypass_candle", "Bypass candle / momentum-agreement filter"],
                  ["favorite_hold_tier3_bypass_open_price", "Bypass open-price direction gate (+ cushion)"],
                  ["favorite_hold_tier3_bypass_prev_windows", "Bypass previous-windows direction gate"],
                  ["favorite_hold_tier3_bypass_kronos", "Bypass Kronos forecast gate"],
                  ["favorite_hold_tier3_bypass_momentum", "Bypass odds-momentum gate"],
                  ["favorite_hold_tier3_bypass_pre_order", "Bypass pre-order metrics filter"],
                  ["favorite_hold_tier3_bypass_time_ranges", "Bypass trade-time ranges gate"],
                  ["favorite_hold_tier3_bypass_min_odds", "Bypass min odds bound"],
                ] as const).map(([key, label]) => (
                  <FieldRow key={key}>
                    <Switch
                      label={label}
                      description={FAVORITE_HOLD_FIELD_DESCRIPTIONS[key]}
                      checked={currentForm[key]}
                      onChange={(e) => setForm((prev) => (prev ? {
                        ...prev,
                        [key]: e.currentTarget.checked,
                      } : prev))}
                    />
                  </FieldRow>
                ))}
              </>
            )}
          </Stack>
        );
      case "kronos_check_enabled":
        return (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Gate entries on the Kronos foundation-model forecast for the
              window. Requires the <Code style={monoStyle}>kronos-service</Code>{" "}
              running and forecasts precomputed into the{" "}
              <Code style={monoStyle}>kronos_predictions</Code> cache (see
              README). Windows with no cached forecast fail closed (skip).
              The inference params below form the forecast cache signature —
              keep them fixed across a sweep and sweep the confidence /
              direction knobs instead.
            </Text>
            <FieldRow>
              <Switch
                label="Require direction match"
                description={KRONOS_FIELD_DESCRIPTIONS.kronos_require_direction}
                checked={currentForm.kronos_require_direction}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev, kronos_require_direction: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Min confidence"
                description={KRONOS_FIELD_DESCRIPTIONS.kronos_min_confidence}
                min={0}
                max={1}
                step={0.01}
                decimalScale={3}
                value={currentForm.kronos_min_confidence}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, kronos_min_confidence: Math.min(1, Math.max(0, Number(v) || 0)),
                } : prev))}
              />
              <NumberInput
                label="Min edge over entry ask"
                description="Require Kronos P(picked) >= rounded entry ask + this (e.g. 0.03). Paying 0.87 on a 0.79 forecast is negative EV. 0 disables. Valid: decimal 0.0 – 1.0. Recommended: 0.02 – 0.05."
                min={0}
                max={1}
                step={0.01}
                decimalScale={3}
                value={currentForm.kronos_min_edge_over_ask}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, kronos_min_edge_over_ask: Math.min(1, Math.max(0, Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
            <Divider label="Forecast mode" />
            <FieldRow>
              <Switch
                label="Settled windows only (forecast at window open)"
                description={KRONOS_FIELD_DESCRIPTIONS.kronos_settled_precompute_enabled}
                checked={currentForm.kronos_settled_precompute_enabled}
                onChange={(e) => setForm((prev) => (prev ? {
                  ...prev, kronos_settled_precompute_enabled: e.currentTarget.checked,
                } : prev))}
              />
            </FieldRow>
            {currentForm.kronos_settled_precompute_enabled && (
              <FieldRow>
                <NumberInput
                  label="Precompute offset (seconds into window)"
                  description={KRONOS_FIELD_DESCRIPTIONS.kronos_precompute_offset_seconds}
                  min={0}
                  value={currentForm.kronos_precompute_offset_seconds}
                  onChange={(v) => setForm((prev) => (prev ? {
                    ...prev, kronos_precompute_offset_seconds: Math.max(0, Math.floor(Number(v) || 0)),
                  } : prev))}
                />
              </FieldRow>
            )}
            {!currentForm.kronos_settled_precompute_enabled && (
              <>
                <FieldRow>
                  <Switch
                    label="Dynamic cache warming (warm before decision time)"
                    description={KRONOS_FIELD_DESCRIPTIONS.kronos_dynamic_warm_enabled}
                    checked={currentForm.kronos_dynamic_warm_enabled}
                    onChange={(e) => setForm((prev) => (prev ? {
                      ...prev, kronos_dynamic_warm_enabled: e.currentTarget.checked,
                    } : prev))}
                  />
                </FieldRow>
                {currentForm.kronos_dynamic_warm_enabled && (
                  <FieldRow>
                    <NumberInput
                      label="Warm safety buffer (seconds)"
                      description={KRONOS_FIELD_DESCRIPTIONS.kronos_dynamic_warm_buffer_seconds}
                      min={0}
                      value={currentForm.kronos_dynamic_warm_buffer_seconds}
                      onChange={(v) => setForm((prev) => (prev ? {
                        ...prev, kronos_dynamic_warm_buffer_seconds: Math.max(0, Math.floor(Number(v) || 0)),
                      } : prev))}
                    />
                  </FieldRow>
                )}
              </>
            )}
            <Divider label="Inference (cache signature — keep fixed across a sweep)" />
            <Text size="xs" c="dimmed">{KRONOS_SIGNATURE_HINT}</Text>
            <KronosCachePicker
              marketId={marketId}
              currentSignature={kronosSignatureFromParams({
                model: currentForm.kronos_model,
                lookback: currentForm.kronos_lookback,
                timeframe: currentForm.kronos_candle_timeframe_seconds,
                sampleCount: currentForm.kronos_sample_count,
                temperature: currentForm.kronos_temperature,
                topP: currentForm.kronos_top_p,
                settled: currentForm.kronos_settled_precompute_enabled,
              })}
              currentDecisionTime={parseDecisionTime(currentForm.decision_time)}
              onApply={(info) =>
                setForm((prev) => (prev ? {
                  ...prev,
                  kronos_model: info.model,
                  kronos_lookback: info.lookback,
                  kronos_candle_timeframe_seconds: info.timeframe,
                  kronos_sample_count: info.sample_count,
                  kronos_temperature: info.temperature,
                  kronos_top_p: info.top_p,
                  // Adopt the cache's MODE so the config signature matches it
                  // (a settled cache needs the settled flag on, else the gate
                  // would look up the decide-late signature and cold-start).
                  kronos_settled_precompute_enabled: info.settled,
                  decision_time: formatDecisionTime(info.decision_time_seconds),
                } : prev))
              }
            />
            <FieldRow>
              <Select
                label="Model"
                description={KRONOS_FIELD_DESCRIPTIONS.kronos_model}
                data={KRONOS_MODEL_OPTIONS}
                value={currentForm.kronos_model}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, kronos_model: v || "small",
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Lookback (context candles)"
                description={KRONOS_FIELD_DESCRIPTIONS.kronos_lookback}
                min={1}
                max={512}
                value={currentForm.kronos_lookback}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, kronos_lookback: Math.max(1, Math.floor(Number(v) || 0)),
                } : prev))}
              />
              <NumberInput
                label="Context candle timeframe (s)"
                description={KRONOS_FIELD_DESCRIPTIONS.kronos_candle_timeframe_seconds}
                min={1}
                value={currentForm.kronos_candle_timeframe_seconds}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, kronos_candle_timeframe_seconds: Math.max(1, Math.floor(Number(v) || 0)),
                } : prev))}
              />
            </FieldRow>
            <FieldRow>
              <NumberInput
                label="Sample count"
                description={KRONOS_FIELD_DESCRIPTIONS.kronos_sample_count}
                min={1}
                max={64}
                value={currentForm.kronos_sample_count}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, kronos_sample_count: Math.max(1, Math.floor(Number(v) || 0)),
                } : prev))}
              />
              <NumberInput
                label="Temperature (T)"
                description={KRONOS_FIELD_DESCRIPTIONS.kronos_temperature}
                min={0.01}
                step={0.05}
                decimalScale={2}
                value={currentForm.kronos_temperature}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, kronos_temperature: Math.max(0.01, Number(v) || 1.0),
                } : prev))}
              />
              <NumberInput
                label="Top-p"
                description={KRONOS_FIELD_DESCRIPTIONS.kronos_top_p}
                min={0.01}
                max={1}
                step={0.05}
                decimalScale={2}
                value={currentForm.kronos_top_p}
                onChange={(v) => setForm((prev) => (prev ? {
                  ...prev, kronos_top_p: Math.min(1, Math.max(0.01, Number(v) || 0.9)),
                } : prev))}
              />
            </FieldRow>
          </Stack>
        );
      default:
        return null;
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>Bot Configuration — {marketLabel}</Title>
        <Group>
          {/* V3 multi-market: pills reflect THIS market's per-market
              activation. Markets with no row in
              ``config_activation_per_market`` are in data-collection-
              only mode — surface that clearly instead of showing the
              btc-5m legacy pointer's IDs. */}
          {activeCfg ? (
            <>
              <Badge color="cyan">active: {activeCfg.config_version_id.slice(0, 18)}…</Badge>
              {activeCfg.applies_from_window_start ? (
                <Badge variant="light" color="gray" leftSection={<IconClock size={12} />}>
                  applies from {new Date(activeCfg.applies_from_window_start).toLocaleString()}
                </Badge>
              ) : null}
            </>
          ) : (
            <Badge color="yellow" variant="light">
              data-collection only — no active config
            </Badge>
          )}
        </Group>
      </Group>

      {savedVersionId ? (
        <Alert color="green" title="New config version created and activated">
          <Stack gap={4}>
            <Text size="sm">id: <Code style={monoStyle}>{savedVersionId}</Code></Text>
            <Text size="xs" c="dimmed">This snapshot is now the active config for upcoming windows.</Text>
          </Stack>
        </Alert>
      ) : null}

      <Alert color={activeCfg ? "blue" : "yellow"} title="Save behavior">
        {activeCfg ? (
          <>
            Saving creates a new config snapshot and activates it for{" "}
            <b>{marketLabel}</b> starting at the next window boundary. Other
            markets are unaffected.
          </>
        ) : (
          <>
            <b>{marketLabel}</b> currently has no active config — it is in
            data-collection-only mode (feed + ticks persisted, no orders
            placed). Saving will create the first config snapshot for this
            market and <b>promote it to live trading</b> at the next window
            boundary. Other markets are unaffected.
          </>
        )}
      </Alert>

      {errs.length > 0 ? (
        <Alert color="red" icon={<IconAlertCircle />} title="Form invalid">
          {errs.map((e) => <Text key={e} size="sm">• {e}</Text>)}
        </Alert>
      ) : null}

      <style>{`
        .config-form-card .mantine-InputWrapper-label,
        .config-form-card .mantine-Switch-label,
        .config-form-card .mantine-Checkbox-label {
          font-size: 1rem;
          font-weight: 650;
        }

        .config-form-card .mantine-InputWrapper-description,
        .config-form-card .mantine-Switch-description,
        .config-form-card .mantine-Checkbox-description {
          font-size: 0.9rem;
        }
      `}</style>

      <Grid className="config-form-card" gutter="md" maw={1200} w="100%" mx="auto" align="stretch">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder radius="md" padding="md" h="100%">
            <Stack gap="md">
              <Title order={4}>Bot / Core Configuration</Title>
              <FieldRow>
                <TextInput
                  label="Config Name"
                  description="Internal snapshot name for identifying this strategy profile in history."
                  placeholder="e.g. baseline-live-v2"
                  value={form.config_name}
                  onChange={(e) => setForm({ ...form, config_name: e.currentTarget.value })}
                />
              </FieldRow>
              <FieldRow>
                <TextInput
                  label="Display Timezone"
                  description="IANA timezone for dashboard labels (for example, America/New_York)."
                  value={form.display_timezone}
                  onChange={(e) => setForm({ ...form, display_timezone: e.currentTarget.value })}
                />
              </FieldRow>
              <FieldRow>
                <TextInput
                  label="Trade Size (USDC)"
                  description="Base amount per order. If Cascading Sizes is enabled, this is used only as fallback. Valid: decimal greater than 0 (e.g. 10 or 12.50). Recommended: start small, 5 – 25 USDC."
                  value={form.trade_size_usdc}
                  onChange={(e) => setForm({ ...form, trade_size_usdc: e.currentTarget.value })}
                />
              </FieldRow>
              <FieldRow>
                <TextInput
                  label="Minimum Odds"
                  description="Lower entry gate. Bot only enters if chosen side odds are at or above this value. Valid: decimal 0.0 – 0.99 (must be below 1 and at or below Maximum Odds). Recommended: 0.60 – 0.75 (default 0.65)."
                  value={form.min_odds}
                  onChange={(e) => setForm({ ...form, min_odds: e.currentTarget.value })}
                />
              </FieldRow>
              <FieldRow>
                <TextInput
                  label="Maximum Odds"
                  description="Upper entry gate. Keeps entries out of overextended prices above this threshold. Valid: decimal 0.01 – 1.0 (must be above 0 and at or above Minimum Odds). Recommended: 0.90 – 0.96 (default 0.95)."
                  value={form.max_odds}
                  onChange={(e) => setForm({ ...form, max_odds: e.currentTarget.value })}
                />
              </FieldRow>
              <FieldRow>
                <NumberInput
                  label="Win Streak Max Loss Threshold (USDC)"
                  description="Max loss amount (USDC) the bot is allowed to take while still continuing the active win streak. Any resolved loss at or above this amount resets the streak; losses strictly below it are treated as neutral. 0 disables — every loss resets the streak. Note: Momentum Sell SL has its own setting (Loss-side sell breaks win streak) that controls whether momentum-sell losses break the streak. Valid: decimal 0.0 or more (USDC). Recommended: 0 (off), or 10 – 20% of your trade size."
                  min={0}
                  step={0.01}
                  decimalScale={4}
                  value={form.loss_streak_continue_threshold_usdc}
                  onChange={(v) => setForm({ ...form, loss_streak_continue_threshold_usdc: Number(v) })}
                />
              </FieldRow>
              <FieldRow>
                <NumberInput
                  label="No-buy Cutoff (seconds left)"
                  description="Hard block near close. If seconds left are below this, no new buy is allowed. 0 disables. Valid: whole seconds 0 – 300. Recommended: 10 – 30."
                  min={0}
                  max={300}
                  value={form.no_buy_window_seconds}
                  onChange={(v) => setForm({ ...form, no_buy_window_seconds: Number(v) })}
                />
              </FieldRow>
              <FieldRow>
                <NumberInput
                  label="No-sell Cutoff (seconds left)"
                  description="Hard block near close. If seconds left are at or below this, take-profit and momentum-sell exits are suppressed (stop-loss still fires). 0 disables. Valid: whole seconds 0 – 300. Recommended: 5 – 15."
                  min={0}
                  max={300}
                  value={form.no_sell_window_seconds}
                  onChange={(v) => setForm({ ...form, no_sell_window_seconds: Number(v) })}
                />
              </FieldRow>
              <FieldRow>
                <TextInput
                  label="Decision Time (M:SS)"
                  description="When to start evaluating entries in each window (for example, 4:00). Valid: M:SS from 0:00 up to the window length (5:00 on 5m, 15:00 on 15m). Recommended: 75 – 80% of the window (default 3:54 on 5m)."
                  value={form.decision_time}
                  onChange={(e) => setForm({ ...form, decision_time: e.currentTarget.value })}
                />
              </FieldRow>
              <FieldRow>
                <Textarea
                  label="Notes"
                  autosize
                  minRows={2}
                  placeholder="Optional change notes (recorded with the snapshot)"
                  value={notes}
                  onChange={(e) => setNotes(e.currentTarget.value)}
                />
              </FieldRow>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder radius="md" padding="md" h="100%">
            <Stack gap="md">
              <Title order={4}>Polymarket Settings</Title>
              <FieldRow>
                <Select
                  label="Price Feed"
                  description="Live market data source used by the strategy and dashboard updates."
                  value={form.price_feed}
                  onChange={(v) => setForm({ ...form, price_feed: v ?? "polymarket_chainlink" })}
                  data={[{ value: "polymarket_chainlink", label: "Polymarket Chainlink" }]}
                  allowDeselect={false}
                />
              </FieldRow>
              <FieldRow>
                <Select
                  label="Order Type"
                  description="FOK fills immediately or cancels; GTC can rest on the order book."
                  value={form.order_type}
                  onChange={(v) => setForm({ ...form, order_type: v ?? "FOK" })}
                  data={[
                    { value: "FOK", label: "Fill or Kill (FOK)" },
                    { value: "GTC", label: "Good Til Cancelled (GTC)" },
                  ]}
                  allowDeselect={false}
                />
              </FieldRow>
              {form.order_type === "GTC" ? (
                <>
                  <FieldRow>
                    <Switch
                      label="Auto-cancel GTC on condition drift"
                      description="When enabled, unfilled GTC entries are cancelled as soon as strategy conditions no longer pass."
                      checked={form.gtc_auto_cancel_on_drift}
                      onChange={(e) => setForm({ ...form, gtc_auto_cancel_on_drift: e.currentTarget.checked })}
                    />
                  </FieldRow>
                  {!form.gtc_auto_cancel_on_drift ? (
                    <>
                      <FieldRow>
                        <NumberInput
                          label="GTC Recheck Interval (seconds)"
                          description="How often to re-check unfilled GTC entries for timeout-based cancellation. Valid: whole seconds 1 – 60. Recommended: 2 – 5 (default 2)."
                          min={1}
                          max={60}
                          value={form.gtc_recheck_seconds}
                          onChange={(v) => setForm({ ...form, gtc_recheck_seconds: Number(v) })}
                        />
                      </FieldRow>
                      <FieldRow>
                        <NumberInput
                          label="Cancel Unfilled GTC After (seconds)"
                          description="If still unfilled after this duration, cancel and allow a fresh entry when conditions pass again. Valid: whole seconds 1 – 300. Recommended: 10 – 30 (default 15)."
                          min={1}
                          max={300}
                          value={form.gtc_cancel_unfilled_after_seconds}
                          onChange={(v) => setForm({ ...form, gtc_cancel_unfilled_after_seconds: Number(v) })}
                        />
                      </FieldRow>
                    </>
                  ) : null}
                </>
              ) : null}
              <FieldRow>
                <NumberInput
                  label="Slippage Ticks"
                  description="Extra price buffer in exchange tick increments to improve fill probability. Valid: whole number 0 – 20. Recommended: 1 – 2 (default 1)."
                  min={0}
                  max={20}
                  value={form.slippage_ticks}
                  onChange={(v) => setForm({ ...form, slippage_ticks: Number(v) })}
                />
              </FieldRow>
              <FieldRow>
                <Select
                  label="Exit Order Type"
                  description="Order type used for sells (stop-loss, momentum sell, reactive take-profit, manual). INHERIT = same as the entry order type. FOK/FAK cross the book immediately so emergency exits do not rest unfilled."
                  value={form.exit_order_type}
                  onChange={(v) => setForm({ ...form, exit_order_type: (v ?? "INHERIT").toUpperCase() })}
                  data={[
                    { value: "INHERIT", label: "Inherit entry order type" },
                    { value: "FOK", label: "FOK (fill-or-kill)" },
                    { value: "FAK", label: "FAK (fill-and-kill)" },
                    { value: "GTC", label: "GTC (rest on book)" },
                  ]}
                  allowDeselect={false}
                />
                <NumberInput
                  label="Exit Slippage Ticks"
                  description="Tick buffer BELOW the bid applied to protective sells (stop-loss / momentum sell) so they cross a falling bid. -1 = inherit Slippage Ticks. Valid: whole number -1 – 20. Recommended: -1, or 1 – 3."
                  min={-1}
                  max={20}
                  value={form.exit_slippage_ticks}
                  onChange={(v) => setForm({ ...form, exit_slippage_ticks: Math.max(-1, Math.trunc(Number(v) || 0)) })}
                />
              </FieldRow>
              <FieldRow>
                <Switch
                  label="Allow Multiple Orders per Window"
                  description="When disabled (default), at most one order is placed per window: further placements are blocked once any order for the window has been bought and sold or resolved (matching the simulation engines). When enabled, the bot may enter a new position in the same window after a previous order has been bought and sold. Manual buys always bypass this gate."
                  checked={form.allow_multiple_orders_per_window}
                  onChange={(e) => setForm({ ...form, allow_multiple_orders_per_window: e.currentTarget.checked })}
                />
              </FieldRow>
              <FieldRow>
                <NumberInput
                  label="Entry Confirmation Delay (seconds)"
                  description="Live + Sim: consecutive seconds all entry gates must remain passing in the same direction before the bot places the order. 0 = place immediately on the first passing tick. Valid: whole seconds 0 – 300. Recommended: 0 – 5."
                  min={0}
                  max={300}
                  value={form.entry_confirm_seconds}
                  onChange={(v) => setForm({ ...form, entry_confirm_seconds: Math.max(0, Math.trunc(Number(v) || 0)) })}
                />
              </FieldRow>
              <FieldRow>
                <TextInput
                  label="Fee Rate"
                  description="Fallback analytical fee estimate used for P/L when exact fill breakdown is unavailable. Valid: decimal 0.0 – 1.0 (fraction, e.g. 0.072 = 7.2%). Recommended: 0.072 (default)."
                  value={form.fee_rate}
                  onChange={(e) => setForm({ ...form, fee_rate: e.currentTarget.value })}
                />
              </FieldRow>
              <FieldRow>
                <NumberInput
                  label="Fee Exponent"
                  description="Exponent applied in fallback fee estimate. Keep at 1 unless intentionally calibrating. Valid: number 0 – 10. Recommended: 1 (default)."
                  min={0}
                  max={10}
                  value={form.fee_exponent}
                  onChange={(v) => setForm({ ...form, fee_exponent: Number(v) })}
                />
              </FieldRow>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder radius="md" padding="md" h="100%">
            <Stack gap="md">
              <Title order={4}>Testing / Debug Configuration</Title>
              <div>
                <Group justify="space-between" align="flex-start" wrap="nowrap" py="sm">
                  <div>
                    <Text fw={600}>Test Mode</Text>
                    <Text size="sm" c="dimmed">When enabled, strategy runs and persists normally but does not submit live CLOB orders.</Text>
                  </div>
                  <Switch
                    mt={2}
                    checked={form.dry_run}
                    onChange={(e) => setForm({ ...form, dry_run: e.currentTarget.checked })}
                  />
                </Group>
                <Divider />
              </div>
              <FieldRow>
                <Select
                  label="Log Level"
                  description="Python logger level."
                  value={form.log_level}
                  onChange={(value) => setForm({ ...form, log_level: value ?? "INFO" })}
                  data={[
                    { value: "DEBUG", label: "DEBUG" },
                    { value: "INFO", label: "INFO" },
                    { value: "WARNING", label: "WARNING" },
                    { value: "ERROR", label: "ERROR" },
                    { value: "CRITICAL", label: "CRITICAL" },
                  ]}
                  allowDeselect={false}
                />
              </FieldRow>
              <FieldRow>
                <TextInput
                  label="Log File"
                  description="Path to bot runtime log output file."
                  value={form.log_file}
                  onChange={(e) => setForm({ ...form, log_file: e.currentTarget.value })}
                />
              </FieldRow>
              <FieldRow>
                <TextInput
                  label="State File"
                  description="Path to local state snapshot file used by the bot."
                  value={form.state_file}
                  onChange={(e) => setForm({ ...form, state_file: e.currentTarget.value })}
                />
              </FieldRow>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder radius="md" padding="md" h="100%">
            <Stack gap="md">
              <Title order={4}>Custom Features</Title>
              <Text size="sm" c="dimmed">Enable a feature to automatically expand its settings below.</Text>
              <Stack gap={0}>
                {FEATURE_TOGGLES.map((feature, index) => (
                  <div key={feature.key}>
                    <Group justify="space-between" align="flex-start" wrap="nowrap" py="sm">
                      <div>
                        <Text fw={600}>{feature.title}</Text>
                        <Text size="sm" c="dimmed">{feature.description}</Text>
                        <FeatureMetaBadges metaKey={feature.key} size="xs" />
                      </div>
                      <Switch
                        mt={2}
                        checked={form[feature.key]}
                        onChange={(e) => updateFeatureToggle(feature.key, e.currentTarget.checked)}
                      />
                    </Group>
                    <Collapse in={openFeaturePanels.includes(feature.key)}>{renderFeatureSettings(feature.key)}</Collapse>
                    {index < FEATURE_TOGGLES.length - 1 ? <Divider /> : null}
                  </div>
                ))}
              </Stack>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      <Group mt="sm" justify="flex-end" maw={1200} w="100%" mx="auto">
        <Button
          size="lg"
          variant="light"
          color="grape"
          leftSection={<IconUpload size={18} />}
          onClick={handleUploadConfig}
        >
          Upload Config (JSON)
        </Button>
        <Button
          size="lg"
          leftSection={<IconDeviceFloppy size={18} />}
          disabled={errs.length > 0 || saveVersion.isPending}
          onClick={() => saveVersion.mutate(toConfig(form))}
        >
          Save new version
        </Button>
      </Group>

      {saveVersion.isError ? (
        <Alert color="red" mt="md" title="Save failed">
          {(saveVersion.error as ApiClientError | Error).message}
        </Alert>
      ) : null}
    </Stack>
  );
}
